import log from "loglevel";
import * as BABYLON from "@babylonjs/core";
import { Camera } from "@babylonjs/core";
import axios, { AxiosResponse } from "axios";
import { AxiosError } from "axios";
import { api } from './TokenManager';
import {
  Scene,
  Vector3,
  StandardMaterial,
  Color3,
  Color4,
  MeshBuilder,
  ActionManager,
  ExecuteCodeAction,
  ArcRotateCamera,
  HemisphericLight,
  Engine,
} from "@babylonjs/core";
import * as GUI from "@babylonjs/gui";
import { useState } from "react";

const CURRENT_RUN_ID_STORAGE_KEY = "currentRunId";

export function setCurrentRunId(runId: string): void {
  const normalizedRunId = String(runId || "").trim();
  if (!normalizedRunId) {
    throw new Error("Cannot store an empty runId.");
  }
  sessionStorage.setItem(CURRENT_RUN_ID_STORAGE_KEY, normalizedRunId);
  localStorage.setItem("runId", normalizedRunId);
}

export function getCurrentRunId(): string {
  const runId = String(
    sessionStorage.getItem(CURRENT_RUN_ID_STORAGE_KEY) ||
      localStorage.getItem(CURRENT_RUN_ID_STORAGE_KEY) ||
      localStorage.getItem("runId") ||
      ""
  ).trim();

  if (!runId) {
    throw new Error(
      "RUN_ID_REQUIRED: create or activate a packout run before calling run-scoped APIs."
    );
  }

  return runId;
}

function runParams(params: Record<string, unknown> = {}, runId = getCurrentRunId()) {
  return {
    ...params,
    runId,
    currentRunId: runId,
  };
}

export function runBody<T extends Record<string, unknown>>(body: T, runId = getCurrentRunId()) {
  return {
    ...body,
    runId,
    currentRunId: runId,
  };
}

function isRunScopedTable(tableName: string): boolean {
  return [
    "boxesForEdit",
    "placed_boxes",
    "placed_boxes_view",
    "pallet_usage",
    "distributed_items",
  ].includes(tableName);
}

interface Box {
  name: string;
  nsn: string;
  niin: number;
  nomenclature: string;
  qty: number;
  authorizedqty: number;
  weight: number;
  maxsupportedweight?: number;
  length: number;
  width: number;
  height: number;
  quadid?: number; // Optional quadid
  storagetype: string;
  isPlaced: boolean;
  position_no?: number;
}

interface Distributed_Items {
  id?: number;
  name: string;
  nsn: string;
  niin: number;
  nomenclature: string;
  qty: number;
  authorizedqty: number;
  weight: number;
  maxsupportedweight?: number;
  length: number;
  width: number;
  height: number;
  quad_id: number; // Optional quadid
  storagetype: string;
  isPlaced: boolean;
  isSkipped: boolean;
}

interface BoxMetadata {
  id?: number;            // placed_boxes.id, used by InfoBox to PATCH dimensions
  nomenclature: string;
  niin:string;
  name: string;
  nsn: string;
  weight: number;
  maxsupportedweight: number;
  width: number;
  height: number;
  depth: number;
  x: number;
  y: number;
  z: number;
  position_no: number;
  deltasupportweight: number;
}


//Used for expanding the boxesForEdit,
//if quantity = 4, then 4 rows are added to the new array
interface boxesForEdit_Items {
  name: string;
  nsn: string;
  niin: number;
  nomenclature: string;
  authorizedqty: number;
  qty: number; //on hand qty
  weight: number;
  length: number;
  width: number;
  height: number;
  maxsupportedweight: number;
  storagetype: string;
  isPlaced: boolean;
}

interface PlacedBox {
  id: number;
  name: string;
  nsn: string;
  niin: number;
  nomenclature: string;
  qty: number;
  authorizedqty: number;
  weight: number;
  maxsupportedweight?: number;
  length: number;
  width: number;
  height: number;
  level: number;
  levelMaxHeight: number;
  quadrantId: number;
  palletid: number;
  storagetype: string;
  position_no: number;
  x: number;
  y: number;
  z: number;
  spillNo: number;
  rotation: number;
  palletno: number;
}

interface PlacedBox2 {
  id: number;
  box_id: number | null;
  name: string;
  nsn: string;
  niin: string;
  nomenclature: string;
  qty: number;
  authorizedqty: number;
  weight: string;
  maxsupportedweight?: number;
  length: string;
  width: string;
  height: string;
  level: number;
  levelmaxheight: string;
  quadid: number;
  palletid: number;
  storagetype: string;
  position_no: number;
  x: string;
  y: string;
  z: string;
  spillno: number;
  rotation: string;
  palletno: number;
  created_at: string;
}

let selectedBox: any = null; // To store the currently selected box
const palletBoxes: any[] = [];

export function createScene(engine: any, storagetype: string): Scene {
  const scene = new Scene(engine);
  scene.clearColor = new Color4(1, 1, 1, 1); // White background

  let zoom = 0;
  let vector= new Vector3(24, 24, 0);

  if (storagetype == "QuadPallet" || storagetype == "ISU-90" ) {
    zoom = 165;
  } else if( storagetype =="463L"|| storagetype =="C463L") {
    zoom = 278;
  }
  else if( storagetype =="NFEV"){
    vector= new Vector3(40, 24, 0);
    zoom = 270;
  }
  else if( storagetype =="100x120"){
    vector= new Vector3(50, 23, 0);
    zoom = 290;
  }
  else if( storagetype =="80x120"){
    zoom = 240;
    vector= new Vector3(60, 24, 0);
  }
  else {
    zoom = 200;
  }

  const camera = new ArcRotateCamera(
    "Camera",
    Math.PI / 2,
    Math.PI / 3.5,
    zoom,
    vector,
    scene
  );

  // Lock to current vertical angle

  console.log("camera beta: "+camera.beta)
  camera.lowerBetaLimit = camera.beta; 
  camera.upperBetaLimit = camera.beta; 
  camera.panningSensibility = 300;

  // Adjust camera to better view the scene
  camera.attachControl(engine.getRenderingCanvas(), true);


  const p = camera.inputs.attached.pointers;
  
  const light = new HemisphericLight("light1", new Vector3(1, 1, 0), scene);
  light.specular = new BABYLON.Color3(0, 0, 0);

  const light2 = new HemisphericLight("light1", new Vector3(0, 1, 1), scene);
  light2.specular = new BABYLON.Color3(0, 0, 0);

  // Pallet material

  const palletMaterial = new StandardMaterial("palletMaterial", scene);
  palletMaterial.diffuseColor = new Color3(0.44, 0.26, 0.07); // Brown

  palletMaterial.backFaceCulling = false;
  // OR for PBR materials:
  palletMaterial.twoSidedLighting = true;

  // Pallet geometry (4'x4' with a height of 0.5 inches)
  let palletSize = {};
  let palletX = 0;
  let palletZ = 0;
  if (storagetype == "QuadPallet" || storagetype == "ISU-90") {
    palletSize = { height: 0.5, width: 50, depth: 41 };
    palletX = 25;
    palletZ = 20.5;
  } else if (storagetype == "NFEV") {
    //palletSize={ height: 0.5, width: 250, depth: 250 }
    //palletX=125;
    //palletZ=125;
    palletSize = { height: 0.5, width: 98, depth: 98 };
    palletX = 49;
    palletZ = 49;
  } else if (storagetype =="80x120"){
    palletSize = { height: 0.5, width: 120, depth: 80 };
    palletX = 60;
    palletZ = 40;

  } else if (storagetype =="100x120"){
    palletSize = { height: 0.5, width: 120, depth: 100 };
    palletX = 60;
    palletZ = 50;

  } else {
    palletSize = { height: 0.5, width: 84, depth: 108 };
    palletX = 42; //463L
    palletZ = 54; //463l
  }
  const pallet = MeshBuilder.CreateBox("pallet", palletSize, scene);

  //pallet.position.x = 24; // Center pallet on the x-axis
  //pallet.position.z = 24; // Center pallet on the z-axis
  pallet.position.x = palletX;
  pallet.position.z = palletZ;

  pallet.position.y = 0; // Positioning just above the ground
  pallet.material = palletMaterial;

  log.info("Scene created with camera and light");

  // GUI
  /*var advancedTexture = GUI.AdvancedDynamicTexture.CreateFullscreenUI("UI");
  var label = new GUI.TextBlock();
  label.text = "Door";
  label.width = 0.2;
  label.fontSize = 16;
  label.color = "Black";
  advancedTexture.addControl(label);

  label.linkWithMesh(pallet);
  label.linkOffsetY = 100;*/

  const plane = BABYLON.MeshBuilder.CreatePlane(
    "plane",
    { width: 55, height: 35 },
    scene
  );
  plane.parent = pallet;
  plane.position.y = 0;
  plane.rotation.y = Math.PI;
  plane.position.z = 30;
  plane.rotation.x = Math.PI / 2;
  const advancedTexture2 = GUI.AdvancedDynamicTexture.CreateForMesh(plane);
  const button1 = GUI.Button.CreateSimpleButton("but1", "Door");
  button1.width = 215;
  button1.height = 25;
  button1.color = "black";
  button1.fontSize = 176;
  advancedTexture2.addControl(button1);

  return scene;
}

export function clearBoxes(scene: Scene) {
  console.log("All boxes cleared from the scene. TOTAL ", palletBoxes);
  for (const box of palletBoxes) {
    console.log("Clearing box", box.name);
    box.dispose(); // Dispose of each child box
  }
  palletBoxes.length = 0; // Clear the array
  console.log("All boxes cleared from the scene.");
}

let boxClickCallback: ((metadata: BoxMetadata) => void) | undefined = undefined;

export function setBoxClickCallback(callback: (metadata: BoxMetadata) => void) {
  boxClickCallback = callback;
}

export async function addBox(
  scene: Scene,
  x: number,
  y: number,
  z: number,
  weight: number,
  maxsupportedweight: number,
  width: number,
  height: number,
  depth: number,
  colorHex: string,
  nomenclature: string,
  niin: string,
  name: string,
  nsn: string,
  position_no: number,
  // rotation and center are part of the addBoxFunction callback signature
  // used by placeBoxesOnPallet*. They aren't read by addBox today but are
  // declared here so TypeScript can match the shapes. Prefixed _ to flag
  // intentional non-use.
  _rotation: number | undefined,
  deltasupportweight: number,
  _center: boolean | undefined,
  placedBoxId?: number,
) {
  const boxMaterial = new StandardMaterial(name + "Material", scene);

  //Set font type
  const font_type = "Arial";

  //Set width an height for plane
  let planeWidth = height * 0.4;
  const planeHeight = width * 0.8;

  if (position_no > 9) {
    planeWidth = height * 0.5;
  }

  if (height > width) {
    planeWidth = height * 0.2;
  }

  //Set width and height for dynamic texture using same multiplier
  const DTWidth = planeWidth * 60;
  const DTHeight = planeHeight * 45;

  //Set text
  const text = `${position_no}`;

  //Create dynamic texture
  const dynamicTexture = new BABYLON.DynamicTexture(
    "DynamicTexture",
    { width: DTWidth, height: DTHeight },
    scene
  );

  //Check width of text for given font type at any size of font
  const ctx = dynamicTexture.getContext();
  const size = 12; //any value will work
  ctx.font = size + "px " + font_type;
  const textWidth = ctx.measureText(text).width;

  //Calculate ratio of text width to size of font used
  const ratio = textWidth / size;

  //set font to be actually used to write text on dynamic texture
  //var font_size = 12;

  const font_size = Math.floor(DTWidth / (ratio * 1)); //size of multiplier (1) can be adjusted, increase for smaller text
  const font = font_size + "px " + font_type;

  //Draw text
  const color = colorHex;
  dynamicTexture.drawText(text, null, null, font, "#000000", color, true);
  // Apply the dynamic texture to each face of the box
  const faceUV = [
    new BABYLON.Vector4(0, 0, 1, 1), // Front - will have the serial number upright
    new BABYLON.Vector4(0, 0, 1, 1), // Back
    new BABYLON.Vector4(0, 0, 1, 1), // Right
    new BABYLON.Vector4(0, 0, 1, 1), // Left
    new BABYLON.Vector4(1, 1, 0, 0), // Top
    new BABYLON.Vector4(0, 0, 1, 1), // Bottom
  ];

  const box = MeshBuilder.CreateBox(
    "box",
    { width: width, height: height, depth: depth, faceUV: faceUV, wrap: true },
    scene
  );

  // Adjusting position relative to the back left corner of the pallet


  box.position.x = x + width / 2;
  box.position.y = y + height / 2;
  box.position.z = z + depth / 2;

  // Assign the dynamic texture to the front face of the box
  boxMaterial.diffuseTexture = dynamicTexture;
  boxMaterial.backFaceCulling = false;
  // OR for PBR materials:
  boxMaterial.twoSidedLighting = true;
  box.material = boxMaterial;
  box.metadata = {
    id: placedBoxId,
    nomenclature,
    niin,
    name,
    nsn,
    weight,
    maxsupportedweight,
    width,
    height,
    depth,
    x,
    y,
    z,
    position_no,
    deltasupportweight,
  } as BoxMetadata;

  // Enable edge rendering
  box.enableEdgesRendering();
  box.edgesWidth = 12.0;
  box.edgesColor = new Color4(0, 0, 0, 0.8); // Black edges

  // Action Manager for picking the box
  box.actionManager = new ActionManager(scene);
  box.actionManager.registerAction(
    new ExecuteCodeAction(ActionManager.OnPickTrigger, function () {
      console.log("Box clicked");

      // Call the registered callback if it exists
      if (boxClickCallback) {
        boxClickCallback(box.metadata as BoxMetadata);
      }
      // Optional: still keep a selectedBox reference internally if needed
      selectedBox = box;
    })
  );
  palletBoxes.push(box);
  //await new Promise((resolve) => setTimeout(resolve, 2500));
}

export function handleKeyPress(event: KeyboardEvent) {
  if (selectedBox && event.key === "t") {
    if (selectedBox.material.alpha === 1) {
      selectedBox.material.alpha = 0.5; // Make the box transparent
    } else {
      selectedBox.material.alpha = 1; // Restore opacity
    }
  }
}

export async function addPallet(
  printScene: Scene,
  storagetype: string
){
  // Pallet material
  const palletMaterial = new StandardMaterial("palletMaterial", printScene);
  palletMaterial.diffuseColor = new Color3(0.44, 0.26, 0.07); // Brown

  palletMaterial.backFaceCulling = false;
  // OR for PBR materials:
  palletMaterial.twoSidedLighting = true;

  // Pallet geometry (4'x4' with a height of 0.5 inches)
  let palletSize = {};
  let palletX = 0;
  let palletZ = 0;
  if (storagetype == "ISU-90") {
    palletSize = { height: 0.5, width: 50, depth: 41 };
    palletX = 25;
    palletZ = 20.5;
  } else if (storagetype == "NFEV") {
    palletSize = { height: 0.5, width: 99, depth: 99 };
    palletX = 49.5;
    palletZ = 49.5;
    //palletSize={ height: 0.5, width: 250, depth: 250 }
    //palletX=125;
    //palletZ=125;
  } else if (storagetype == "80x120"){
    palletSize = { height: 0.5, width: 120, depth: 80 };
    palletX = 60;
    palletZ = 40;

  } else if (storagetype == "100x120"){
    palletSize = { height: 0.5, width: 120, depth: 100 };
    palletX = 60;
    palletZ = 50;

  } else {
    palletSize = { height: 0.5, width: 84, depth: 108 };
    palletX = 42; //463L
    palletZ = 54; //463l
  }
  const pallet = MeshBuilder.CreateBox("pallet", palletSize, printScene);

  pallet.position.x = palletX;
  pallet.position.z = palletZ;

  pallet.position.y = 0; // Positioning just above the ground
  pallet.material = palletMaterial;

}

export async function saveCurrentCanvas(
  canvas: HTMLCanvasElement | null,
  scene: BABYLON.Scene,
  palletid: number,
  storagetype: string,
  quadrantId: number
) {
  if (!canvas) return;

  const images: string[] = [];
  const overheadImages: string[] = [];

  /* -------------------------------------------------
     Helpers
  ------------------------------------------------- */

  const waitForSceneReady = async (scene: BABYLON.Scene) => {
    // Force Babylon to compute transforms & bounds
    scene.meshes.forEach(mesh => {
      if (!mesh.isDisposed()) {
        mesh.computeWorldMatrix(true);
      }
    });
    scene.render();
    // Wait one real rendered frame
    await new Promise<void>(resolve =>
      requestAnimationFrame(() => resolve())
    );
    
  };

  /* -------------------------------------------------
     Max level
  ------------------------------------------------- */

  const maxlevel = await returnPlaceBoxesMaxLayer(
    storagetype,
    palletid,
    quadrantId
  );
  if (!maxlevel) return;

  /* -------------------------------------------------
     Create canvases
  ------------------------------------------------- */

  const printCanvas = document.createElement("canvas");
  printCanvas.width = canvas.width * 2;
  printCanvas.height = canvas.height * 2;
  document.body.appendChild(printCanvas);

  const overheadCanvas = document.createElement("canvas");
  overheadCanvas.width = canvas.width * 2;
  overheadCanvas.height = canvas.height * 2;
  document.body.appendChild(overheadCanvas);

  /* -------------------------------------------------
     Engines & scenes
  ------------------------------------------------- */

  const engine = new BABYLON.Engine(printCanvas, true);
  const overheadEngine = new BABYLON.Engine(overheadCanvas, true);

  const printScene = new BABYLON.Scene(engine);
  const overheadScene = new BABYLON.Scene(overheadEngine);

  printScene.clearColor = new BABYLON.Color4(0, 0, 0, 0);
  overheadScene.clearColor = new BABYLON.Color4(0, 0, 0, 0);


  let zoom = 250;
  let rotation = 3;
  let rotationb = 4;
  let overheadVector = new BABYLON.Vector3(0, 0, 0);

  if (storagetype === "QuadPallet" || storagetype == "ISU-90") {
    zoom = 150;
    rotation = 4;
    rotationb = 3.62;
    overheadVector = new BABYLON.Vector3(9.2, 9.3, 20.4);
  } else if (storagetype === "100x120" || storagetype === "80x120") {
    zoom = 340;
    overheadVector = new BABYLON.Vector3(50, 24.3, 3.4);
  } else {
    zoom = 670;
    overheadVector = new BABYLON.Vector3(9.2, 24.3, 45.4);
  }

  const printCamera = new BABYLON.ArcRotateCamera(
    "printCamera",
    Math.PI * 1.9,
    Math.PI / rotationb,
    zoom,
    new BABYLON.Vector3(9.2, 24.3, 13.4),
    printScene
  );

  if (storagetype === "QuadPallet" || storagetype == "ISU-90") {
    printCamera.alpha = Math.PI / rotation;
  } else {
    printCamera.radius = zoom * 0.8;
    printCamera.fov = Math.PI / 5;
    printCamera.fovMode = BABYLON.Camera.FOVMODE_HORIZONTAL_FIXED;
    printCamera.beta += Math.PI / 18;
  }

  const overheadCamera = new BABYLON.ArcRotateCamera(
    "overheadCamera",
    Math.PI / 2,
    0,
    150,
    overheadVector,
    overheadScene
  );

  overheadCamera.mode = BABYLON.Camera.ORTHOGRAPHIC_CAMERA;

  if (storagetype === "QuadPallet" || storagetype == "ISU-90") {
    overheadCamera.orthoLeft = -50;
    overheadCamera.orthoRight = 50;
  } else {
    overheadCamera.orthoLeft = -100;
    overheadCamera.orthoRight = 100;
  }

  const ratio = overheadCanvas.height / overheadCanvas.width;
  overheadCamera.orthoTop = overheadCamera.orthoRight * ratio;
  overheadCamera.orthoBottom = overheadCamera.orthoLeft * ratio;

  /* -------------------------------------------------
     Lights
  ------------------------------------------------- */

  scene.lights.forEach(light => {
    const cloned = light.clone(light.name, null);
    if (cloned) {
      printScene.addLight(cloned);
      overheadScene.addLight(
        cloned.clone(light.name + "_overhead", null)!
      );
    }
  });

  /* -------------------------------------------------
     Base pallet
  ------------------------------------------------- */

  addPallet(printScene, storagetype);
  addPallet(overheadScene, storagetype);

  /* -------------------------------------------------
     Start ONE render loop per engine
  ------------------------------------------------- */

  engine.runRenderLoop(() => {
    printScene.render();
  });

  overheadEngine.runRenderLoop(() => {
    overheadScene.render();
  });

  /* -------------------------------------------------
     Incremental level rendering & capture
  ------------------------------------------------- */

  for (let level = 1; level <= maxlevel+1; level++) {
    console.log("Capturing level", level);

    const printMeshStart = printScene.meshes.length;
    const overheadMeshStart = overheadScene.meshes.length;

    if (level <= maxlevel) {
      await placeBoxesOnPalletByLayerDark(
        printScene,
        overheadScene,
        addBox,
        storagetype,
        palletid,
        quadrantId,
        1,
        level,
        true,
      );
    }
    else {
      await placeBoxesOnPalletByLayer(
        printScene,
        overheadScene,
        addBox,
        storagetype,
        palletid,
        quadrantId,
        1,
        level-1,
        true,
      );
    }

    // Ensure Babylon has fully applied all changes
    await waitForSceneReady(printScene);
    await waitForSceneReady(overheadScene);

    // Capture images
    const img = await BABYLON.Tools.CreateScreenshotAsync(
      engine,
      printCamera,
      { precision: 1 }
    );
    images.push(img);

    const overheadImg = await BABYLON.Tools.CreateScreenshotAsync(
      overheadEngine,
      overheadCamera,
      { precision: 1 }
    );
    overheadImages.push(overheadImg);

    // Dispose ONLY meshes added for this level
    printScene.meshes
      .slice(printMeshStart)
      .forEach(mesh => mesh.dispose());

    overheadScene.meshes
      .slice(overheadMeshStart)
      .forEach(mesh => mesh.dispose());
  }

  /* -------------------------------------------------
     Cleanup
  ------------------------------------------------- */

  engine.stopRenderLoop();
  overheadEngine.stopRenderLoop();

  printScene.dispose();
  overheadScene.dispose();

  engine.dispose();
  overheadEngine.dispose();

  printCanvas.remove();
  overheadCanvas.remove();

  console.log("Captured images for all levels:", images.length, overheadImages.length);

  return { images, overheadImages };
}

export async function saveDataForEditing(
  file: File,
  additionalFiles: File[],
  callback: () => void
): Promise<void> {


}

export function saveCSVDataForEditing(
  file: File,
  additionalFiles: File[],
  packoutName: string,   
  newRunId: string,     
  callback?: () => void,
  initialLoadOrder: number = 1 // <-- Added optional starting load order
): Promise<void> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async function (event: ProgressEvent<FileReader>) {
      try {
        const csvData = event.target?.result as string;
        const lines = csvData.split("\n");
        let formattedData = [];

        for (let i = 1; i < lines.length; i++) {
          const data = lines[i]?.match(/(?:[^",]+|"[^"]*")+/g)?.map((item) => item.replace(/^"|"$/g, ""));
          if (data && data.length >= 11) {
            formattedData.push({
              id: i,
              name: data[0],
              nsn: data[0],
              niin: parseFloat(data[1]),
              nomenclature: data[2],
              weight: parseFloat(data[3]),
              length: parseFloat(data[4]),
              width: parseFloat(data[5]),
              height: parseFloat(data[6]),
              authorizedqty: parseFloat(data[7]),
              qty: parseFloat(data[8]),
              storagetype: data[9],
              maxsupportedweight: parseFloat(data[10]),
              loadorder: initialLoadOrder, // Uses absolute load order position
              runid: newRunId,  
              packoutname: packoutName
            });
          }
        }

        if (additionalFiles.length !== 0) {
          formattedData = await processAdditionalFiles(
            additionalFiles, 
            formattedData, 
            packoutName, 
            newRunId, 
            initialLoadOrder + 1 // Pass next expected load order
          );
        }

        setCurrentRunId(newRunId);

        const rootPayload = runBody(
          {
            packoutName,
            boxes: formattedData,
          },
          newRunId
        );

        const response = await api.post(`/api/boxesForEdit`, rootPayload);
        if (callback) callback();
        resolve();
      } catch (error) {
        console.error("Error saving CSV data for editing:", error);
        reject(error);
      }
    };

    reader.onerror = (err) => reject(err);
    reader.readAsText(file);
  });
}

export async function processAdditionalFiles(
  files: File[],
  formattedData: any[],
  packoutName: string,
  newRunId: string,
  startLoadOrder: number = 2 // <-- Added dynamic starting load order parameter
): Promise<any[]> {
  let loadorder: number = startLoadOrder;
  const baseid = formattedData.length;

  for (const additionalFile of files) {
    const additionalReader = new FileReader();

    await new Promise<void>((resolve) => {
      additionalReader.onload = function (event: ProgressEvent<FileReader>) {
        const additionalCsvData = event.target?.result as string;

        if (additionalCsvData) {
          const additionalLines = additionalCsvData.split("\n");

          for (let i = 1; i < additionalLines.length; i++) {
            const data = additionalLines[i]?.match(/(?:[^",]+|"[^"]*")+/g)?.map((item) => item.replace(/^"|"$/g, ""));
            if (data && data.length >= 10) {
              const id: number = i + baseid;
              formattedData.push({
                id,
                name: data[0],
                nsn: data[0],
                niin: parseFloat(data[1]),
                nomenclature: data[2],
                weight: parseFloat(data[3]),
                length: parseFloat(data[4]),
                width: parseFloat(data[5]),
                height: parseFloat(data[6]),
                authorizedqty: parseFloat(data[7]),
                qty: parseFloat(data[8]),
                storagetype: data[9],
                maxsupportedweight: parseFloat(data[10]),
                loadorder, // Accurately increments per secondary file
                runid: newRunId,
                packoutname: packoutName
              });
            }
          }
        }
        resolve();
      };
      additionalReader.readAsText(additionalFile);
    });
    loadorder++;
  }
  return formattedData;
}


export async function saveCSVDataToDB(
  file: File,
  callback: () => void
): Promise<void> {
  const reader = new FileReader();
  reader.onload = async function (event: ProgressEvent<FileReader>) {
    const csvData = event.target?.result as string;
    const lines = csvData.split("\n");
    const formattedData = [];

    for (let i = 1; i < lines.length; i++) {
      const data = lines[i]?.match(/(?:[^",]+|"[^"]*")+/g)?.map((item) => item.replace(/^"|"$/g, ""))
      if (data && data.length >= 9) {
        const nsn: string = data[0];
        const niin: number = parseFloat(data[1]);
        const nomenclature: string = data[2];
        const qty: number = parseFloat(data[3]);
        const authorizedqty: number = parseFloat(data[3]);
        const weight: number = parseFloat(data[4]);
        const length: number = parseFloat(data[5]);
        const width: number = parseFloat(data[6]);
        const height: number = parseFloat(data[7]);
        const storagetype: string = data[8].substring(0, data[8].length - 2);
        console.log("stored type check:"+ storagetype);

        const boxName = `${niin}-${nomenclature}`;
        formattedData.push({
          name: boxName,
          nsn,
          niin,
          nomenclature,
          qty,
          authorizedqty,
          weight,
          length,
          width,
          height,
          storagetype
        });
      }
    }

    try {
      const response = await api.post(`/api/boxes`, formattedData);
      console.log("CSV data saved to database:", response.data);

      if (callback) {
        console.log("Executing callback.");
        callback();
      }
    } catch (error) {
      console.error("Error saving CSV data:", error);
    }
  };

  reader.onerror = (err) => {
    console.error("Error reading file:", err);
  };
  reader.readAsText(file);
}

async function saveBoxesToDatabase(file: any[]): Promise<void> {
  let index = 0;
  let totalWeight = 0;

  // Prepare the data for API call
  const formattedData = [];
  for (const data of file) {
    const {
      nsn,
      niin,
      nomenclature,
      qty,
      weight,
      length,
      width,
      height,
      storagetype,
    } = data;
    const boxName = `${niin}-${nomenclature}`;
    const authorizedqty = qty;

    for (let q = 0; q < qty; q++) {
      formattedData.push({
        name: boxName,
        nsn,
        niin,
        nomenclature,
        qty,
        authorizedqty,
        weight,
        length,
        width,
        height,
        storagetype,
      });
      index++;
      totalWeight += weight;
    }
  }

  // Send the data to the backend
  try {
    const response = await api.post(`/api/boxes`, formattedData);
    console.log("Saved boxes to the database:", response.data);
    console.log(`Total boxes: ${index}, Total weight: ${totalWeight}`);
  } catch (error) {
    console.error("Error saving boxes to the database:", error);
  }
}

export async function loadBoxes(
  file: {
    nsn: string;
    niin: number;
    nomenclature: string;
    qty: number;
    weight: number;
    maxsupportedweight?: number;
    length: number;
    width: number;
    height: number;
    storagetype: string;
  }[],
  callback: () => void
): Promise<void> {
  saveBoxesToDatabase(file);
  addContainers();

  console.log("added boxes into data base");
  if (callback) {
    log.info("Executing callback.");
    callback();
  }
}

export async function addContainers() {
  console.log("Adding containers_master to the database.");

  // Safely retrieve the current active runId
  let runId: string | null = null;
  try {
    runId = getCurrentRunId();
    console.log ("addContainers : ", runId);
  } catch (err) {
    console.warn("addContainers: No active runId found yet. Defaulting to null.", err);
  }

  const newContainers = [
    {
      name: "QuadPallet",
      containerId: 1,
      containertype: "QuadPallet",
      weight: 8280,
      no_of_quadrants: 4,
      width: 42,
      length: 42,
      height: 81,
      poplateCounter: 0,
      runId
    },
        {
      name: "ISU-90",
      containerId: 1,
      containertype: "ISU-90",
      weight: 8280,
      no_of_quadrants: 4,
      width: 42,
      length: 42,
      height: 81,
      poplateCounter: 0,
      runId
    },
    {
      name: "463L",
      containerId: 2,
      containertype: "463L",
      weight: 9645,
      no_of_quadrants: 1,
      width: 84,
      length: 104,
      height: 96,
      poplateCounter: 0,
      runId
    },
    {
      name: "C463L",
      containerId: 3,
      containertype: "C463L",
      weight: 10355,
      no_of_quadrants: 1,
      width: 84,
      length: 104,
      height: 96,
      poplateCounter: 0,
      runId
    },
    {
      name: "NFEV",
      containerId: 4,
      containertype: "NFEV",
      weight: 6613.86, //lbs //3000 kg
      no_of_quadrants: 1,
      width: 98.425, // in
      length: 98.425,
      height: 43.307,
      poplateCounter: 0,
      runId
    },
    {
      name: "80x120",
      containerId: 5,
      containertype: "80x120",
      weight: 1200, //kg
      no_of_quadrants: 1,
      width: 120,
      length: 80,
      height: 300,
      poplateCounter: 0,
      runId
    },
    {
      name: "100x120",
      containerId: 6,
      containertype: "100x120",
      weight: 1200, //kg
      no_of_quadrants: 1,
      width: 120,
      length: 100,
      height: 300,
      poplateCounter: 0,
      runId
    }
  ];

  try {
    // Fetch existing containers from backend (passing runId as a query param if present)
    const response = await api.get(`/api/containers`, {
      params: runId ? { runId } : undefined
    });
    const existingContainers = response.data;

    const history = await api.get(`/api/user_sessions`);
    console.log ("History ", history.data);

    // Extract existing container IDs
    const existingContainerIds = existingContainers.map(
      (container: any) => container.containerId
    );

    // Filter containers to add
    const containersToAdd = newContainers.filter(
      (container) => !existingContainerIds.includes(container.containerId)
    );

    if (containersToAdd.length > 0) {
      console.log("Adding new containers:", containersToAdd);

      // Send payload with containers list and runId attached
      await api.post(
        `/api/containers`,
        {
          containers: containersToAdd
        },
        {
          params: runId ? { runId } : undefined
        }
      );

      console.log("New containers successfully added.");
    } else {
      console.log("No new containers to add.");
    }
  } catch (error) {
    console.error("Error during addContainers:", error);
  }
}

export async function getAllContainersFromStore() {
  log.info("Getting all containers_master from store");
  // Get all values from the designated object store:
  console.log("getting values from store");
  const response = await api.get(`/api/containers_master`);

  const allValues = response.data;

  //in the future we will return all but for now we only have one container
  //return allValues;
  return allValues;
}

export async function getContainersMasterForStorage(storagetype: string) {
  console.log("Getting all containers_master for storage type:", storagetype);

  try {
    // Fetch filtered containers from the backend
    // In your frontend code
    const response = await api.get(`/api/containers/${storagetype}`);

    const filteredValues = response.data;

    console.log("getContainersMasterForStorage:", filteredValues);

    return filteredValues;
  } catch (error) {
    console.error("Error fetching containers_master:", error);
    throw error;
  }
}

export async function getAllContainersUpdateFromStore() {
  console.log("Getting all containers from the backend.");

  try {
    // Fetch all containers from the backend
    const runId = getCurrentRunId();
    const response = await api.get(`/api/containers/placed`, {
      params: runId ? { runId } : undefined
    });
    const allValues = response.data;

    console.log("Fetched containers from the backend:", allValues);

    return allValues;
  } catch (error) {
    console.error("Error fetching containers:", error);
    throw error;
  }
}

export async function getAllItemsFromPlacedBoxes() {
  console.log("Getting items from the placed_boxes store via the backend.");

  try {
    // Fetch all items from the backend
    const response = await api.get(`/api/placed_boxes`, { params: runParams() });
    const allValues = response.data;

    console.log("Fetched items from placed_boxes:", allValues);

    return allValues;
  } catch (error) {
    console.error("Error fetching items from placed_boxes:", error);
    throw error;
  }
}

export async function getAllItemsFromEditStore() {
  console.log("Getting items from the edit store via the backend.");

  try {
    // Fetch all items from the backend
    const response = await api.get(`/api/boxesForEdit`, { params: runParams() });
    const allValues = response.data;

    console.log("Fetched items from boxesForEdit:", allValues);

    return allValues;
  } catch (error) {
    console.error("Error fetching items from boxesForEdit:", error);
    throw error;
  }
}

export async function updateItemsInEditStore(
  updatedItems: boxesForEdit_Items[]
) {
  console.log("Updating items in the edit store via the backend.");

  try {
    // Send the updated items to the backend for updating
    const response = await api.put(`/api/boxesForEdit`, runBody({ boxes: updatedItems, items: updatedItems }));
    console.log("Items updated successfully:", response.data);
  } catch (error) {
    console.error("Error updating items:", error);
    throw error;
  }
}

export async function saveNewItemsAPI(
  newItems: boxesForEdit_Items[]
): Promise<boxesForEdit_Items[]> {
  console.log("Saving new items via the backend.");

  try {
    // Define the expected response type for better type-safety.
    // We expect the backend to return an array of the created items.
    const response: AxiosResponse<boxesForEdit_Items[]> = await api.post(
      `/api/boxesForEdit`,
      runBody({ boxes: newItems })
    );

    console.log("Items saved successfully:", response.data);

    // The 'if' check for status is removed because axios handles non-2xx statuses
    // by throwing an error, which is caught by the 'catch' block.

    // Directly return the data from the response.
    return response.data;
    
  } catch (error) {
    console.error("Error saving items:", error);
    // Re-throw the error so the calling component can handle it (e.g., show a UI message).
    throw error;
  }
}

export async function getAllItemsFromDistributedStore() {
  console.log("Getting items from the distributed store via the backend.");

  try {
    // Fetch all items from the backend
    const response = await api.get(`/api/distributed_items`, { params: runParams() });
    const allValues = response.data;

    console.log("Fetched items from distributed_items:", allValues);

    return allValues;
  } catch (error) {
    console.error("Error fetching items from distributed_items:", error);
    throw error;
  }
}

/*export async function getAllItemsPlacedBoxesStore() {
  console.log("Getting items from the placed_boxes store via the backend.");

  try {
    // Fetch all items from the backend
    const response = await api.get(`/api/placed_boxes`, { params: runParams() });
    const allValues = response.data;

    console.log("Fetched items from placed_boxes:", allValues);

    return allValues;
  } catch (error) {
    console.error("Error fetching items from placed_boxes:", error);
    throw error;
  }
}*/

export async function getAllItemsPlacedBoxesStore(runId: string): Promise<PlacedBox2[]> {
  try {
      console.log(`Fetching placed boxes view for run ID: ${runId}`);
      
      // Pass the active runId as a query parameter
      const response = await api.get<PlacedBox2[]>(`/api/placed_boxes_view`, {
          params: runParams({}, runId)
      });
      
      const allValues = response.data;
      console.log("Fetched items from placed_boxes for current run:", allValues);

      return allValues;
  } catch (error) {
      console.error("Error fetching items from placed_boxes:", error);
      throw error;
  }
}


export async function readDataFromTable(
  tableName: "boxes" | "distributed_items" | "boxesForEdit"
): Promise<Box[]> {
  console.log("BEGIN readDataFromTable");

  try {
    const response = await api.get(
      `/api/${tableName}`,
      isRunScopedTable(tableName) ? { params: runParams() } : undefined
    );
    const rows = response.data;

    // Map the rows to the desired structure
    return rows.map((row: any) => ({
      name: row.name,
      nsn: row.nsn,
      niin: row.niin,
      nomenclature: row.nomenclature,
      qty: row.qty,
      authorizedqty: row.authorizedqty,
      weight: row.weight,
      maxsupportedweight: row.maxsupportedweight,
      length: row.length,
      width: row.width,
      height: row.height || 0, // Provide a default value if undefined
      storagetype: row.storagetype,
      isPlaced: false,
    }));
  } catch (error) {
    console.error("Error reading data from table:", error);
    throw error; // Re-throw the error to be handled by the caller
  }
}

export function distributeItems(
  items: Box[],
  numBuckets: number,
  targetStorageType: string
): Box[][] {
  console.log("BEGIN distributeItems: NEW", {
    items,
    numBuckets,
    targetStorageType,
  });

  // Validate inputs
  if (!items || !Array.isArray(items)) {
    console.error("Invalid items array:", items);
    return [];
  }
  if (numBuckets <= 0) {
    console.error("Invalid number of buckets:", numBuckets);
    return [];
  }

  // Initialize buckets and weights as numbers
  const buckets: Box[][] = Array.from({ length: numBuckets }, () => []);
  const totalWeights: number[] = Array.from({ length: numBuckets }, () => 0);


  // Distribution logic
  items.forEach((item, idx) => {
    if (!item.storagetype || item.weight == null) {
      console.warn(`Invalid item at index ${idx}:`, item);
      return;
    }

    // Convert weight to number if it's not already
    const weight =
      typeof item.weight === "number"
        ? item.weight
        : parseFloat(item.weight as unknown as string);

    if (isNaN(weight)) {
      console.error(`Invalid weight for item at index ${idx}:`, item.weight);
      return;
    }

    if (item.storagetype === targetStorageType) {
      const minWeight = Math.min(...totalWeights);

      // Validate minWeight
      if (!Number.isFinite(minWeight)) {
        console.error("Invalid minimum weight value:", minWeight);
        return;
      }

      // Ensure totalWeights contains only numbers
      console.log("Total weights before finding index:", totalWeights);
      console.log("Minimum weight:", minWeight);

      const index = totalWeights.indexOf(minWeight);

      // Validate index
      if (index === -1) {
        console.error(
          "Could not find index for minimum weight:",
          minWeight,
          totalWeights
        );
        return;
      }

      buckets[index].push(item);
      totalWeights[index] += weight; // Use converted weight
      console.log(`Item added to bucket ${index}:`, item);
    }
  });

  // Final logs
  console.log("Final Buckets:", buckets);
  console.log("Final Total Weights:", totalWeights);

  return buckets;
}

/**
 * Inserts distributed data into the backend API
 * @param buckets - Array of buckets, each containing Box[] items
 * @returns Promise<void>
 */

export async function insertDistributedData(buckets: Box[][]): Promise<void> {
  console.log("BEGIN insertDistributedData", buckets);

  try {
    // Prepare data with quadid
    const itemsToInsert = buckets.flatMap((bucket, quadid) =>
      bucket.map((item) => ({
        ...item,
        quadid: quadid + 1,
        isPlaced: false,
        isSkipped: false,
        runId: getCurrentRunId(),
      }))
    );

    console.log("Inserting distributed data via backend API:", itemsToInsert);

    // Make an API call to insert distributed data
    const response = await api.post(
      `/api/distributed_items`,
      itemsToInsert
    );

    console.log("Distributed data inserted successfully:", response.data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error(
        "Axios error inserting distributed data:",
        error.message,
        error.response?.data
      );
    } else if (error instanceof Error) {
      console.error("General error inserting distributed data:", error.message);
    } else {
      console.error("Unexpected error inserting distributed data:", error);
    }
    throw error;
  }
}

export async function addDataToTable(
  tableName: string,
  newItem: any
): Promise<void> {
  console.log("BEGIN addDataToTable", tableName, newItem);

  try {
    const payload = isRunScopedTable(tableName)
      ? Array.isArray(newItem)
        ? runBody({ items: newItem, boxes: newItem })
        : runBody({ ...(newItem || {}) })
      : newItem;

    const response = await api.post(`/api/${tableName}`, payload);
    console.log("Request successful:", response.data);

    console.log("Transaction completed.");
  } catch (error) {
    console.error("Transaction not completed due to error:", error);
    throw error;
  }

  console.log("END addDataToTable", tableName, newItem);
}

function pollJobStatus(jobId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    // 15 minutes = 900 seconds. At 3s polling interval = 300 attempts max.
    const maxAttempts = 600; 
    let attempts = 0;

    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await api.get(`/api/job/${jobId}`);
        const { status, message } = res.data;

        if (status === 'completed') {
          clearInterval(interval);
          console.log('Job finished successfully!');
          resolve(); // Resolves the outer Promise so createDistributedTable can proceed
        } else if (status === 'failed') {
          clearInterval(interval);
          console.error('Job failed:', message);
          reject(new Error(message || 'Job execution failed on backend'));
        }
      } catch (err) {
        if (attempts >= maxAttempts) {
          clearInterval(interval);
          console.error('Polling timed out.');
          reject(new Error('Job status polling timed out after 15 minutes.'));
        }
        // TokenManager interceptor handles 401s automatically during transient network errors.
      }
    }, 3000); // Poll every 3 seconds
  });
}

export interface PackingPreferences {
  integrityOption?: string; // kit_integrity | nsn_integrity | kit_and_nsn_integrity | no_integrity
  useShelves?: boolean;
  parameters?: { smallOnTop?: boolean; largeBehindSmall?: boolean };
}

export async function createDistributedTable(
  threshold: number,
  preferences?: PackingPreferences | (() => void),
  callback?: () => void
): Promise<void> {
  // Backward compatible: older call sites pass the callback as the 2nd arg.
  if (typeof preferences === "function") {
    callback = preferences;
    preferences = undefined;
  }
  try {
    console.log("createDistributedTable FROM Utils - threshold:", threshold, "preferences:", preferences);
    const response = await api.post('/api/createDistributedTable', {
      params: runParams({ threshold, packingPreferences: preferences ?? null }),
    });
    const { jobId } = response.data;
    console.log("Job started:", jobId);
    
    // Await will now properly block execution until the Promise in pollJobStatus resolves!
    await pollJobStatus(jobId);
    
    console.log("Job completed");
    if (callback) {
      callback();
    }
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
}

export const getSelectedContainerDetails = async (
  selectedPallet: string
): Promise<any[]> => {
  console.log("getSelectedContainerDetails:", selectedPallet);

  try {
    // Fetch filtered rows from the backend
    const response = await api.get(`/api/containers/${selectedPallet}`);

    console.log("Filtered containers retrieved:", response.data.length);
    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      console.error(
        "Error fetching selected container details:",
        error.message
      );
    } else {
      console.error("Unexpected error:", error);
    }
    throw error;
  }
};

export async function identifyAndAssignLevels(
  quadrantId: number
): Promise<void> {
  console.log("BEGIN identifyAndAssignLevels");

  try {
    // Send a request to the backend to identify and assign levels
    await api.put(`/api/placed_boxes/assignLevels`, runBody({ quadrantId }));

    console.log("Levels assigned successfully.");
  } catch (error) {
    if (error instanceof Error) {
      console.error("Error identifying and assigning levels:", error.message);
    } else {
      console.error("Unexpected error:", error);
    }
    throw error;
  }
}

export async function identifyAndAssignPositions(
  quadrantId: number
): Promise<void> {
  console.log("BEGIN identifyAndAssignPositions");

  try {
    // Send a request to the backend to identify and assign positions
    await api.put(`/api/placed_boxes/assignPositions`, runBody({ quadrantId }));

    console.log("Positions assigned successfully.");
  } catch (error) {
    if (error instanceof Error) {
      console.error(
        "Error identifying and assigning positions:",
        error.message
      );
    } else {
      console.error("Unexpected error:", error);
    }
    throw error;
  }
}

export async function generateBoxesPlacements(
  selectedPallet: string,
  threshold: number
): Promise<void> {
  console.log(
    "BEGIN generateBoxesPlacements selectedPallet, threshold  : ",
    selectedPallet,
    threshold
  );

  try {
    // Fetch container details for the selected pallet
    const containerResponse = await api.get(`/api/containers_storagetype`, {
      params: {
        storagetype: selectedPallet,
      },
    });

    const containers_master = containerResponse.data;

    if (containers_master.length === 0) {
      console.log("No rows found for selectedPallet:", selectedPallet);
      return;
    }

    console.log(
      "Create - Containers:",
      containers_master.length,
      containers_master
    );

    // Fetch unplaced boxes for the selected pallet
    const distributedItemsResponse = await api.get(`/api/distributed_items`, { params: runParams() });
    const allBoxes = distributedItemsResponse.data;

    console.log("allBoxes Data ", allBoxes);
    let unplacedBoxes = allBoxes.filter(
      (box: any) => box.storagetype === selectedPallet && !box.isPlaced
    );

    console.log("Total unplaced boxes:", unplacedBoxes.length, unplacedBoxes);

    const palletWidthInches = containers_master[0].width;
    const palletLengthInches = containers_master[0].length;
    const maxPalletHeightInches = containers_master[0].height;
    const maxPalletWeightPounds = containers_master[0].weight;
    const totalPalletVolume =
      palletWidthInches * palletLengthInches * maxPalletHeightInches;
    let currentPalletNo = 1;
    const maxIterations = 10; // Safeguard against infinite loops

    let iterations = 0;

    while (unplacedBoxes.length > 0 && iterations < maxIterations) {
      console.log(
        `Processing pallet ${currentPalletNo}, boxes to place: ${unplacedBoxes.length}`
      );

      let placedBoxes: any[] = [];
      let remainingBoxes: any[] = [];

      console.log("CONTAINER : ", containers_master[0].name);

      if (
        containers_master[0].name === "463L" ||
        containers_master[0].name === "C463L" ||
        containers_master[0].name === "NFEV" ||
        containers_master[0].name === "80x120" ||
        containers_master[0].name === "100x120" 
      ) {
        const result = await loadBoxesToPlacedTable_CenterAlign(
          palletWidthInches,
          palletLengthInches,
          maxPalletHeightInches,
          maxPalletWeightPounds,
          unplacedBoxes,
          currentPalletNo,
          0, // Start with 0 used volume for each new pallet
          totalPalletVolume,
          selectedPallet,
          containers_master[0].no_of_quadrants,
          threshold
        );
        placedBoxes = result.placedBoxes;
        remainingBoxes = result.remainingBoxes;
      } else {
        const result = await loadBoxesToPlacedTable(
          palletWidthInches,
          palletLengthInches,
          maxPalletHeightInches,
          maxPalletWeightPounds,
          unplacedBoxes,
          currentPalletNo,
          0, // Start with 0 used volume for each new pallet
          totalPalletVolume,
          selectedPallet,
          containers_master[0].no_of_quadrants,
          threshold
        );
        placedBoxes = result.placedBoxes;
        remainingBoxes = result.remainingBoxes;
      }

      // Update placed boxes in the database
      await updatePlacedBoxes(placedBoxes, currentPalletNo);

      // Update unplacedBoxes to only include remaining boxes
      unplacedBoxes = remainingBoxes as Distributed_Items[];

      console.log(
        `Placed ${placedBoxes.length} boxes on pallet ${currentPalletNo}`
      );
      console.log(`Remaining boxes: ${unplacedBoxes.length}`);

      // Move to the next pallet
      currentPalletNo++;
      iterations++;

      if (iterations === maxIterations) {
        console.error(
          `Max iterations reached. Some boxes may remain unplaced.`
        );
      }
    }

    console.log("Box placement completed for all pallets");
    console.log(`Total unplaced boxes remaining: ${unplacedBoxes.length}`);
  } catch (error) {
    console.error("Error in generateBoxesPlacements:", error);
  }
}

export function transformPositionsByQuad(
  quadid: number,
  x: number,
  y: number,
  z: number
): { x: number; y: number; z: number } {
  switch (quadid) {
    case 1:
      return { x, y, z };
    case 2:
      return { x: x - 50, y, z };
    case 3:
      return { x, y, z: z - 41 };
    case 4:
      return { x: x - 50, y, z: z - 41 };
    default:
      return { x, y, z };
  }
}

export async function placeBoxesOnPalletByLayer(
  scene: any,
  overheadScene: any,
  addBoxFunction: (
    scene: any,
    x: number,
    y: number,
    z: number,
    weight: number,
    maxsupportedweight: number,
    width: number,
    height: number,
    length: number,
    colorHex: string,
    nomenclature: string,
    niin: string,
    name: string,
    nsn: string,
    position_no: number,
    rotation: number,
    deltasupportweight:number,
    center:boolean,
    placedBoxId?: number
  ) => void,
  storagetype: string,
  palletid: number,
  quadid: number,
  boxCount: number,
  levelNo: number,
  ifPrint: boolean
): Promise<number | undefined> {

  function getRandomBrightColor(): string {
    const getRandomChannelValue = (): number => {
      const min = 180;
      const max = 255;
      return Math.floor(Math.random() * (max - min + 1)) + min;
    };

    const rgbToHex = (r: number, g: number, b: number): string => {
      return (
        "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")
      );
    };

    return rgbToHex(
      getRandomChannelValue(),
      getRandomChannelValue(),
      getRandomChannelValue()
    );
  }

  try {

     // Determine the correct backend port or domain using standard Create React App envs


      // 1. Get the current authentication login sessionId from storage or state
      const runId = getCurrentRunId(); 


    // Fetch placed boxes for the specified pallet and quadrant
    const response = await api.get(`/api/placed_boxes_view/forpallet`, {
      params: runParams({ palletid, storagetype, quadid }, runId),
    });
    const rows = response.data;

    for (const box of rows) {
      if (box.level <= levelNo && box.quadid == quadid) {
        boxCount++;
        console.log(
          "Placing box:placeBoxesOnPalletByLayer " + box.nomenclature,
          box.level,
          levelNo
        );
        let colorHex = "#ffffff";
        if (box.level == levelNo) {
          colorHex = getRandomBrightColor();
        }
        if (box.niin == "9999") {
          colorHex= "#00ff44ff";
        }
        if (ifPrint == true) {
          colorHex = getRandomBrightColor();
        }
        console.log("Color hex:" + colorHex);

        let center= true;
        if (storagetype == "QuadPallet" || storagetype == "ISU-90"){
          center=false;
        }

        const { x: newX, y: newY, z: newZ } = transformPositionsByQuad(quadid, box.x, box.y, box.z);

        await addBoxFunction(
          scene,
          Number(newX),
          Number(newY),
          Number(newZ),
          Number(box.weight),
          Number(box.maxsupportedweight),
          Number(box.width),
          Number(box.height),
          Number(box.length),
          colorHex,
          box.nomenclature,
          box.niin,
          box.name,
          box.nsn,
          Number(box.position_no),
          box.rotation,
          box.deltasupportweight,
          center,
          Number(box.placed_box_id ?? box.id) // placed_boxes PK (view rows: placed_box_id; view.id is the view's own PK)
        );

        await addBoxFunction(
          overheadScene,
          Number(newX),
          Number(newY),
          Number(newZ),
          Number(box.weight),
          Number(box.maxsupportedweight),
          Number(box.width),
          Number(box.height),
          Number(box.length),
          colorHex,
          box.nomenclature,
          box.niin,
          box.name,
          box.nsn,
          Number(box.position_no),
          box.rotation,
          box.deltasupportweight,
          center,
          Number(box.placed_box_id ?? box.id) // placed_boxes PK (view rows: placed_box_id; view.id is the view's own PK)
        );

        console.log("Position updated for box:", box);

      }
    }

    /*scene.onReadyObservable.add(() => {
      //const camera = scene.activeCamera as BABYLON.ArcRotateCamera;
    });*/
    return boxCount;
  } catch (error) {
    console.error("Error placing boxes on pallet:", error);
  }
}

export async function placeBoxesOnPalletByLayerDark(
  scene: any,
  overheadScene: any,
  addBoxFunction: (
    scene: any,
    x: number,
    y: number,
    z: number,
    weight: number,
    maxsupportedweight: number,
    width: number,
    height: number,
    length: number,
    colorHex: string,
    nomenclature: string,
    niin: string,
    name: string,
    nsn: string,
    position_no: number,
    rotation: number,
    deltasupportweight:number,
    center:boolean,
    placedBoxId?: number
  ) => void,
  storagetype: string,
  palletid: number,
  quadid: number,
  boxCount: number,
  levelNo: number,
  ifPrint: boolean
): Promise<number | undefined> {

  function getRandomBrightColor(): string {
    const getRandomChannelValue = (): number => {
      const min = 180;
      const max = 255;
      return Math.floor(Math.random() * (max - min + 1)) + min;
    };

    const rgbToHex = (r: number, g: number, b: number): string => {
      return (
        "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")
      );
    };

    return rgbToHex(
      getRandomChannelValue(),
      getRandomChannelValue(),
      getRandomChannelValue()
    );
  }

  try {

     // Determine the correct backend port or domain using standard Create React App envs


      // 1. Get the current authentication login sessionId from storage or state
      const runId = getCurrentRunId(); 


    // Fetch placed boxes for the specified pallet and quadrant
    const response = await api.get(`/api/placed_boxes_view/forpallet`, {
      params: runParams({ palletid, storagetype, quadid }, runId),
    });
    const rows = response.data;

    for (const box of rows) {
      if (box.level <= levelNo && box.quadid == quadid) {
        boxCount++;
        console.log(
          "Placing box:placeBoxesOnPalletByLayer " + box.nomenclature,
          box.level,
          levelNo
        );
        let colorHex = "#4f4d4d";
        if (box.level == levelNo) {
          colorHex = getRandomBrightColor();
        }
        if (box.niin == "9999") {
          colorHex= "#00ff44ff";
        }
        if (ifPrint == true && box.level==levelNo) {
          colorHex = getRandomBrightColor();
        }
        console.log("Color hex:" + colorHex);

        let center= true;
        if (storagetype == "QuadPallet" || storagetype == "ISU-90"){
          center=false;
        }

        const { x: newX, y: newY, z: newZ } = transformPositionsByQuad(quadid, box.x, box.y, box.z);

        await addBoxFunction(
          scene,
          Number(newX),
          Number(newY),
          Number(newZ),
          Number(box.weight),
          Number(box.maxsupportedweight),
          Number(box.width),
          Number(box.height),
          Number(box.length),
          colorHex,
          box.nomenclature,
          box.niin,
          box.name,
          box.nsn,
          Number(box.position_no),
          box.rotation,
          box.deltasupportweight,
          center,
          Number(box.placed_box_id ?? box.id) // placed_boxes PK (view rows: placed_box_id; view.id is the view's own PK)
        );

        await addBoxFunction(
          overheadScene,
          Number(newX),
          Number(newY),
          Number(newZ),
          Number(box.weight),
          Number(box.maxsupportedweight),
          Number(box.width),
          Number(box.height),
          Number(box.length),
          colorHex,
          box.nomenclature,
          box.niin,
          box.name,
          box.nsn,
          Number(box.position_no),
          box.rotation,
          box.deltasupportweight,
          center,
          Number(box.placed_box_id ?? box.id) // placed_boxes PK (view rows: placed_box_id; view.id is the view's own PK)
        );

        console.log("Position updated for box:", box);

      }
    }

    /*scene.onReadyObservable.add(() => {
      //const camera = scene.activeCamera as BABYLON.ArcRotateCamera;
    });*/
    return boxCount;
  } catch (error) {
    console.error("Error placing boxes on pallet:", error);
  }
}

export async function returnPlaceBoxesByLayer(
  storagetype: string,
  palletid: number,
  quadrantId: number,
  levelNo: number
): Promise<any[]> {
  console.log("Begin returnPlaceBoxesByLayer", palletid, quadrantId, levelNo);

  try {
    const runId = getCurrentRunId();

    // Fetch all placed boxes for the specified container/quadrant
    const response = await api.get(`/api/placed_boxes_view/forpallet`, {
      params: runParams(
        {
          palletid,
          quadid: quadrantId,
          quadrantId,
          storagetype,
        },
        runId
      ),
    });

    const allRows = response.data || [];

    // Filter by level (comparing both string and number representations)
    const layerRows = allRows.filter((row: any) => {
      const rowLevel = String(row.level);
      const targetLevel = String(levelNo);
      return rowLevel === targetLevel;
    });

    console.log(`Did I get rows for level ${levelNo}:`, layerRows.length);
    return layerRows;
  } catch (error) {
    console.error("Error fetching rows by layer:", error);
    throw error;
  }
}

interface MaxLayerResponse {
  maxLevel: number;
}

export async function returnPlaceBoxesMaxLayer(
  storagetype: string,
  palletid: number,
  quadrantId: number
): Promise<number> {
  console.log("Begin returnPlaceBoxesMaxLayer", {
    palletid,
    quadrantId,
    storagetype,
  });

  try {

      // Determine the correct backend port or domain using standard Create React App envs


      // 1. Get the current authentication login sessionId from storage or state
      const currentRunId = getCurrentRunId();

    // Fetch the maximum level from the backend
    console.log("params: " + palletid + " " + quadrantId + " " + storagetype);
    if (typeof storagetype === "string") {
      const response = await api.get(`/api/placed_boxes_view/maxLayer`, {
        params: runParams({ palletid, quadrantId, storagetype }, currentRunId),
      });

      const maxLevelResponse: { maxlayer: number }[] = response.data;
      const maxLevel = maxLevelResponse[0]?.maxlayer;
      console.log("Max level retrieved:" + maxLevel);
      return maxLevel;
    }
    return 1;
  } catch (error) {
    if (error instanceof Error) {
      console.error("Error fetching max level:", error.message);
    } else {
      console.error("Unexpected error:", error);
    }

    // Return a default value if an error occurs
    return 0;
  }
}

function normalizeKeys(data: any[]): PlacedBox[] {
  return data.map((box) => ({
    id: box.id,
    name: box.name,
    nsn: box.nsn,
    niin: box.niin,
    nomenclature: box.nomenclature,
    qty: box.qty,
    authorizedqty: box.authorizedqty, // Match to PlacedBox
    weight: parseFloat(box.weight),
    length: parseFloat(box.length),
    width: parseFloat(box.width),
    height: parseFloat(box.height),
    level: box.level,
    levelMaxHeight: parseFloat(box.levelmaxheight), // Match to PlacedBox
    quadrantId: box.quadid, // Match to PlacedBox
    palletid: box.palletid, // Match to PlacedBox
    storagetype: box.storagetype, // Match to PlacedBox
    position_no: box.position_no,
    x: parseFloat(box.x),
    y: parseFloat(box.y),
    z: parseFloat(box.z),
    spillNo: box.spillno,
    rotation: parseFloat(box.rotation),
    palletno: box.palletno,
    created_at: box.created_at, // Add any additional properties as needed
  }));
}

export async function updatePositions(runId: string) {
  try {




    //const response = await api.get(`/api/placed_boxes_view/maxLayer`

    const { data: rawPlacedBoxes } = await api.get(`/api/placed_boxes_view`, {
        params: runParams({}, runId)
      });

    // Normalize the data to match PlacedBox interface
    const allPlacedBoxes: PlacedBox[] = normalizeKeys(rawPlacedBoxes);

    if (!allPlacedBoxes || allPlacedBoxes.length === 0) {
      console.log("No placed boxes found.");
      return;
    }

    console.log("allPlacedBoxes ", allPlacedBoxes);

    // Step 2: Group boxes by storagetype, PalletNo, and quadrantId
    // Group the data
    const grouped: Record<
      string,
      {
        storagetype: string;
        PalletNo: number;
        quadid: number;
        names: Set<string>;
      }
    > = allPlacedBoxes.reduce(
      (
        acc: Record<
          string,
          {
            storagetype: string;
            PalletNo: number;
            quadid: number;
            names: Set<string>;
          }
        >,
        box: PlacedBox
      ) => {
        const key = `${box.storagetype}-${box.palletno}-${box.quadrantId}`;

        if (!acc[key]) {
          acc[key] = {
            storagetype: box.storagetype,
            PalletNo: box.palletno,
            quadid: box.quadrantId,
            names: new Set<string>(),
          };
        }
        acc[key].names.add(box.name);
        return acc;
      },
      {}
    );

    console.log("GROUPD ", grouped);
    // Step 3: Transform the grouped object into an array and count names
    const result = Object.values(grouped).map((group1) => ({
      placedStorageType: group1.storagetype,
      PalletNo: group1.PalletNo,
      quadrantId: group1.quadid,
      count: group1.names.size,
    }));

    console.log("RESULT : ", result);
    // Step 4: Process each group
    for (const { placedStorageType, PalletNo, quadrantId } of result) {
      console.log(
        `Storage Type: ${placedStorageType}, Pallet No: ${PalletNo}, Quadrant ID: ${quadrantId}`
      );

      // Fetch rows for the specific PalletNo via API
      // Fetch and normalize the data
      //const { data: rawRows } = await api.get(`/api/placed_boxes/byPallet/${PalletNo}`);

      const response = await api.get(`/api/placed_boxes_view/forpallet`, {
        params: runParams(
          {
            palletid: PalletNo,
            storagetype: placedStorageType,
            quadid: quadrantId,
          },
          runId
        ),
      });

      const allRows = response.data;

      // Normalize the data to match the interface
      //const allRows: PlacedBox[] = rawRows.map((row: { storagetype: any; storagetype: any; quadrantid: any; quadrantId: any; }) => ({
      //  ...row,
      //  // Convert database fields to match interface
      //  storagetype: row.storagetype || row.storagetype,
      //  quadrantId: row.quadrantId || row.quadrantId,
      //  // Add other fields that might need case conversion
      //}));

      console.log("ALL allRows ", allRows);
      // Filter the rows based on quadrantId and storagetype
      const filteredRows = allRows.filter(
        (row: { storagetype: string; quadid: number }) =>
          row.storagetype === placedStorageType && row.quadid === quadrantId
      );
      console.log("ALL filteredRows ", filteredRows);
      // First create a new array copy to avoid mutating the original
      const sortedRows = [...filteredRows].sort((a, b) => {
        // Add null checks and default values
        const levelA = a.level || 0;
        const levelB = b.level || 0;
        const xA = a.x || 0;
        const xB = b.x || 0;
        const zA = a.z || 0;
        const zB = b.z || 0;

        // Compare levels first
        if (levelA !== levelB) {
          return levelA - levelB;
        }

        // If levels are equal, compare x values
        if (xA !== xB) {
          return xA - xB;
        }

        // If x values are equal, compare z values
        return zA - zB;
      });
      console.log("ALL ROWS ", sortedRows);
      // Update placed_boxes table with the positions
      let previousY: number | null = null;
      let currentLevel = 0;
      let boxCount = 1;

      for (const box of sortedRows) {
        box.position_no = boxCount++;
        if (box.y !== previousY) {
          currentLevel++;
          previousY = box.y;
        }
        box.level = currentLevel;

        // update rows via API
        await api.put(`/api/placed_boxes/${box.id}`, runBody({ ...box }, runId));
      }
    }
  } catch (error) {
    console.error("Error updating positions for placed boxes:", error);
  }
}


export async function cleanUp() {
  localStorage.removeItem("tabPointer");
  localStorage.removeItem("palletVar");
  localStorage.removeItem("storeVar");
  try {
    await api.post(`/api/cleanup`);
  } catch (err) {
    console.error("Error emptying db:", err);
  }
}

function addCylinderFunction(
  scene: any, x: number, y: number, z: number,
  weight: number, maxweight: number,
  diameter: number, height: number, // Map values passed from main function
  colorHex: string, nomenclature: string, niin: string,
  name: string, nsn: string, position_no: number,
  rotation: number, deltasupportweight: number,
  center: boolean, placedBoxId?: number
) {
  
  // 1. Create the Cylinder mesh
  // Babylon cylinders stand vertically by default, meaning they sit on their flat side.
  const cylinder = MeshBuilder.CreateCylinder("cylinder_" + placedBoxId, {
    height: height,
    diameter: diameter
  }, scene);

  // 2. Apply Position (Adjusting for Babylon's center-origin if 'center' logic requires it)
  cylinder.position.set(x, y + (height / 2), z); // Adds half height so it sits *on* the surface coordinate
  cylinder.rotation.y = rotation;

  // 3. Create Material & Color
  const material = new StandardMaterial("cylMat_" + placedBoxId, scene);
  material.diffuseColor = Color3.FromHexString(colorHex);
  material.backFaceCulling = false;
  // OR for PBR materials:
  material.twoSidedLighting = true;
  cylinder.material = material;

  // 4. Print Position Number on the Top Flat Circle Side
  // We use a DynamicTexture mapped to a separate plane on top of the cylinder, or directly onto it.
  const textureSize = 256;
  const dynamicTexture = new BABYLON.DynamicTexture("dynamicText", textureSize, scene, true);
  
  const textMaterial = new StandardMaterial("textMat", scene);
  textMaterial.diffuseTexture = dynamicTexture;

  // Draw text onto the texture
  const font = "bold 70px Arial";
  dynamicTexture.drawText(
    position_no.toString(), 
    null, // center X
    null, // center Y
    font, 
    "black", 
    "white", // background color
    true
  );

  // Create a flat disc/plane to act as the "cap" containing the number
  const textCap = MeshBuilder.CreateDisc("textCap", { radius: diameter / 2 }, scene);
  textCap.position.set(x, y + height + 0.01, z); // slightly above the top of the cylinder to avoid z-fighting
  textCap.rotation.x = Math.PI / 2; // Flip flat
  textCap.material = textMaterial;
  
  // Parent it to the cylinder so it moves/rotates with it
  textCap.setParent(cylinder);
}

export async function placeBoxesOnPallet(
  scene: any,
  addBoxFunction: (
    scene: any,
    x: number,
    y: number,
    z: number,
    weight: number,
    maxsupportedweight:number,
    width: number,
    height: number,
    length: number,
    colorHex: string,
    nomenclature: string,
    niin:string,
    name: string,
    nsn:string,
    position_no: number,
    rotation: number,
    deltasupportweight:number,
    center: boolean,
    placedBoxId?: number,
  ) => void,
  palletid: number,
  storagetype: string,
  quadid: number
): Promise<void> {
  console.log("Begin placeBoxesOnPallet", {
    palletid,
    storagetype,
    quadid,
  });

  // Helper function to generate random bright colors
  function getRandomBrightColor(): string {
    const getRandomChannelValue = (): number => {
      const min = 180;
      const max = 255;
      return Math.floor(Math.random() * (max - min + 1)) + min;
    };

    const rgbToHex = (r: number, g: number, b: number): string => {
      return (
        "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")
      );
    };

    return rgbToHex(
      getRandomChannelValue(),
      getRandomChannelValue(),
      getRandomChannelValue()
    );
  }

  try {



        const runId = getCurrentRunId();
      // 1. Get the current authentication login sessionId from storage or state 
    // Fetch and sort rows from the backend
    const response = await api.get(`/api/placed_boxes_view/forpallet`, {
      params: runParams({ palletid, storagetype, quadid }, runId),
    });
    const rows = response.data;

    console.log("Placing boxes for", storagetype, quadid, rows);


    let center= true;
    if (storagetype == "QuadPallet" || storagetype == "ISU-90"){
      center=false;
    }

    const updatedBoxes = [];

    // Render and update boxes
    for (const box of rows) {
      console.log("Placing box:", box);

      let colorHex = getRandomBrightColor();
      if (box.niin == "9999") {
          colorHex= "#00ff44ff";
      }
      //box.position_no = boxCount++;
      const { x: newX, y: newY, z: newZ } = transformPositionsByQuad(quadid, box.x, box.y, box.z);

      console.log("Add box ", box);
      // Render the box in the Babylon.js scene
      addBoxFunction(
        scene,
        Number(newX),
        Number(newY),
        Number(newZ),
        Number(box.weight),
        Number(box.maxsupportedweight),
        Number(box.width),
        Number(box.height),
        Number(box.length),
        colorHex,
        box.nomenclature,
        box.niin,
        box.name,
        box.nsn,
        Number(box.position_no),
        box.rotation,
        box.deltasupportweight,
        center,
        Number(box.placed_box_id ?? box.id) // placed_boxes PK (view rows: placed_box_id; view.id is the view's own PK)
      );

      updatedBoxes.push({
        id: box.id,
        position_no: Number(box.position_no),
      });

      console.log("Position updated for box:", box);
    }

    // Update positions in the backend
    //await api.put(`/api/placed_boxes/updatePositions`, updatedBoxes);
    const currentRunId = getCurrentRunId();
    await updatePositions(currentRunId);

    console.log("All positions updated successfully");
  } catch (error) {
    console.error("Error placing boxes on pallet:", error);
  }
}

export async function placeBoxesOnPalletwithLevel(
  scene: any,
  addBoxFunction: (
    scene: any,
    x: number,
    y: number,
    z: number,
    weight: number,
    maxsupportedweight:number,
    width: number,
    height: number,
    length: number,
    colorHex: string,
    nomenclature: string,
    niin: string,
    name: string,
    nsn: string,
    position_no: number,
    rotation: number,
    deltasupportweight: number,
    center: boolean,
    placedBoxId?: number,
  ) => void,
  palletid: number,
  storagetype: string,
  quadid: number,
  levelNo: number,
): Promise<void> {
  console.log("Begin placeBoxesOnPalletwithLevel", {
    palletid,
    storagetype,
    quadid,
    levelNo
  });

  // Helper function to generate random bright colors
  function getRandomBrightColor(): string {
    const getRandomChannelValue = (): number => {
      const min = 180;
      const max = 255;
      return Math.floor(Math.random() * (max - min + 1)) + min;
    };

    const rgbToHex = (r: number, g: number, b: number): string => {
      return (
        "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")
      );
    };

    return rgbToHex(
      getRandomChannelValue(),
      getRandomChannelValue(),
      getRandomChannelValue()
    );
  }

  try {



      // 1. Get the current authentication login sessionId from storage or state
      const runId = getCurrentRunId(); 

    // Fetch and sort rows from the backend
    const response = await api.get(`/api/placed_boxes_view/forpallet`, {
      params: runParams({ palletid, storagetype, quadid }, runId),
    });
    const rows = response.data;

    console.log("Placing boxes for", storagetype, quadid, rows);


    let center= true;
    if (storagetype == "QuadPallet" || storagetype == "ISU-90"){
      center=false;
    }

    const updatedBoxes = [];

    //const filteredBoxes = rows.filter((box:any) => box.level > levelNo);
    // Render and update boxes
    for (const box of rows) {
      if(box.level<=levelNo){
        let colorHex= "#e7e7e7"
        if (box.level==levelNo){
          colorHex = getRandomBrightColor();
        }
        const { x: newX, y: newY, z: newZ } = transformPositionsByQuad(quadid, box.x, box.y, box.z);

        addBoxFunction(
          scene,
          Number(newX),
          Number(newY),
          Number(newZ),
          Number(box.weight),
          Number(box.maxsupportedweight),
          Number(box.width),
          Number(box.height),
          Number(box.length),
          colorHex,
          box.nomenclature,
          box.niin,
          box.name,
          box.nsn,
          Number(box.position_no),
          box.rotation,
          box.deltasupportweight,
          center,
          Number(box.placed_box_id ?? box.id) // placed_boxes PK (view rows: placed_box_id; view.id is the view's own PK)
        );
  
        updatedBoxes.push({
          id: box.id,
          position_no: Number(box.position_no),
        });
        }
  
      // Update positions in the backend
      //await api.put(`/api/placed_boxes/updatePositions`, updatedBoxes);
    }

    const currentRunId = getCurrentRunId();

    await updatePositions(currentRunId);


  } catch (error) {
    console.error("Error placing boxes on pallet:", error);
  }
}

export async function placeBoxesOnPalletHighlightAddons(
  scene: any,
  addBoxFunction: (
    scene: any,
    x: number,
    y: number,
    z: number,
    weight: number,
    maxsupportedweight: number,
    width: number,
    height: number,
    length: number,
    colorHex: string,
    nomenclature: string,
    niin: string,
    name: string,
    nsn: string,
    position_no: number,
    rotation: number,
    deltasupportweight: number,
    center: boolean,
    placedBoxId?: number,
  ) => void,
  palletid: number,
  storagetype: string,
  quadid: number,
  lastLoadOutNum: number,
): Promise<void> {
  console.log("Begin placeBoxesOnPallet", {
    palletid,
    storagetype,
    quadid,
  });

  try {



      // 1. Get the current authentication login sessionId from storage or state
      const runId = getCurrentRunId(); 


    // Fetch and sort rows from the backend
    const response = await api.get(`/api/placed_boxes_view/forpallet`, {
      params: runParams({ palletid, storagetype, quadid }, runId),
    });
    const rows = response.data;

    console.log("Placing boxes for", storagetype, quadid, rows);
    console.log("loadordernum:", lastLoadOutNum);

    let center= true;
    if (storagetype == "QuadPallet" || storagetype == "ISU-90"){
      center=false;
    }

    const updatedBoxes = [];

    // Render and update boxes
    for (const box of rows) {
      let colorHex = "#e7e7e7";
      if (box.loadorder==lastLoadOutNum){
        colorHex= "#FF0000";
      }
      if (box.niin == "9999") {
          colorHex= "#00ff44ff";
      }
      const { x: newX, y: newY, z: newZ } = transformPositionsByQuad(quadid, box.x, box.y, box.z);

      
      // Render the box in the Babylon.js scene
      addBoxFunction(
        scene,
        Number(newX),
        Number(newY),
        Number(newZ),
        Number(box.weight),
        Number(box.maxsupportedweight),
        Number(box.width),
        Number(box.height),
        Number(box.length),
        colorHex,
        box.nomenclature,
        box.niin,
        box.name,
        box.nsn,
        Number(box.position_no),
        box.rotation,
        box.deltasupportweight,
        center,
        Number(box.placed_box_id ?? box.id) // placed_boxes PK (view rows: placed_box_id; view.id is the view's own PK)
      );

      updatedBoxes.push({
        id: box.id,
        position_no: Number(box.position_no),
      });

      console.log("Position updated for box:", box);
    }

    // Update positions in the backend
    //await api.put(`/api/placed_boxes/updatePositions`, updatedBoxes);

    const currentRunId = getCurrentRunId();
    await updatePositions(currentRunId);

    console.log("All positions updated successfully");
  } catch (error) {
    console.error("Error placing boxes on pallet:", error);
  }
}

export async function checkForLastLoadOut(
  palletid: number,
  storagetype: string,
  quadid: number,
  loadorder: number){
    try {
      console.log("Checking for new boxes");
      // Send the update request to the backend
      const response = await api.get(`/api/placed_boxes/loadorder`, {
        params: runParams({ storagetype, palletid, quadid, loadorder })
      });
  
      if (response.status === 200) {
        console.log("Loadorder Box updated successfully");
        const rows = response.data;
        if (rows.length>0){
          return true;
        }
        return false;
      } else {
        throw new Error("Failed to search for new boxes. Please try again.");
      }
    } catch (error) {
    }
}

export async function updateBox(oldBox: any, newBox: any): Promise<void> {
  try {
    console.log("Updating box with new details...");

    // Send the update request to the backend
    const response = await api.put(
      `/api/placed_boxes/updateBox`,
      runBody({ oldBox, newBox })
    );

    if (response.status === 200) {
      console.log("Box updated successfully");
    } else {
      throw new Error("Failed to update the box. Please try again.");
    }
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error:", error.message, error.response?.data);
    } else if (error instanceof Error) {
      console.error("General error:", error.message);
    } else {
      console.error("Unexpected error:", error);
    }
  }
}

export async function addBoxToStore(
  boxName: string,
  boxNsn: string,
  boxNiin: number,
  boxNomenclature: string,
  boxQty: number,
  boxAuthorizedQty: number,
  boxWeight: number,
  boxMaxsupportedweight:number,
  originalBoxLength: number,
  originalBoxWidth: number,
  boxHeight: number,
  levelNumber: number,
  currentY: number,
  quadrantId: number,
  selectedPallet: string,
  storagetype: string,
  boxSerialNumber: number,
  currentX: number,
  currentZ: number,
  orientation: { rotation: number }
): Promise<void> {
  try {
    // Send box data to the backend
    const response = await api.post(
      `/api/placed_boxes_view`,
      runBody({
        name: boxName,
        nsn: boxNsn,
        niin: boxNiin,
        nomenclature: boxNomenclature,
        qty: boxQty,
        authorizedqty: boxAuthorizedQty,
        weight: boxWeight,
        maxsupportedweight: boxMaxsupportedweight,
        length: originalBoxLength,
        width: originalBoxWidth,
        height: boxHeight,
        level: levelNumber,
        levelMaxHeight: currentY + boxHeight,
        quadrantId: quadrantId,
        palletid: parseInt(selectedPallet, 10),
        storagetype: storagetype,
        position_no: boxSerialNumber,
        x: currentX,
        y: currentY,
        z: currentZ,
        spillNo: 1,
        rotation: orientation.rotation,
        palletno: 1,
      })
    );

    console.log(`Box added successfully:`, response.data);
  } catch (error) {
    if (error instanceof Error) {
      console.error("Error fetching total weight:", error.message);
    } else {
      console.error("Unexpected error:", error);
    }
  }
}

// Then define the class implementing the interface
export class PalletSpace {
  private space: boolean[][][];
  private width: number;
  private length: number;
  private height: number;

  constructor(width: number, length: number, height: number) {
    this.width = Math.floor(width);
    this.length = Math.floor(length);
    this.height = Math.floor(height);

    // Initialize 3D space using explicit loops
    this.space = [];
    for (let x = 0; x < this.width; x++) {
      this.space[x] = [];
      for (let y = 0; y < this.height; y++) {
        this.space[x][y] = [];
        for (let z = 0; z < this.length; z++) {
          this.space[x][y][z] = false;
        }
      }
    }
  }

  public calculateSupport(
    x: number,
    y: number,
    z: number,
    width: number,
    length: number
  ): number {
    // Ground level always has full support
    if (y === 0) return 1;

    // Boundary check
    if (
      x < 0 ||
      x + width > this.width ||
      y <= 0 ||
      y >= this.height ||
      z < 0 ||
      z + length > this.length
    ) {
      return 0;
    }

    let supportedPoints = 0;
    const totalPoints = width * length;

    // Check support from the level below
    for (let ix = x; ix < x + width; ix++) {
      for (let iz = z; iz < z + length; iz++) {
        if (this.isSpaceOccupied(ix, y - 1, iz)) {
          supportedPoints++;
        }
      }
    }

    return supportedPoints / totalPoints;
  }

  public hasMinimumSupport(
    x: number,
    y: number,
    z: number,
    width: number,
    length: number,
    minSupport: number
  ): boolean {
    if (y === 0) return true;

    const requiredPoints = Math.ceil(width * length * minSupport);
    let supportedPoints = 0;

    for (let ix = x; ix < x + width; ix++) {
      for (let iz = z; iz < z + length; iz++) {
        if (this.isSpaceOccupied(ix, y - 1, iz)) {
          supportedPoints++;
          if (supportedPoints >= requiredPoints) {
            return true;
          }
        }
      }
    }

    return false;
  }

  public isSpaceOccupied(x: number, y: number, z: number): boolean {
    if (
      x < 0 ||
      x >= this.width ||
      y < 0 ||
      y >= this.height ||
      z < 0 ||
      z >= this.length
    ) {
      return true; // Out of bounds is considered occupied
    }
    return this.space[x][y][z];
  }

  public isSpaceAvailable(
    x: number,
    y: number,
    z: number,
    width: number,
    length: number,
    height: number
  ): boolean {
    // Boundary checks
    if (
      x < 0 ||
      x + width > this.width ||
      y < 0 ||
      y + height > this.height ||
      z < 0 ||
      z + length > this.length
    ) {
      return false;
    }

    // Check every point in the volume
    for (let ix = x; ix < x + width; ix++) {
      for (let iy = y; iy < y + height; iy++) {
        for (let iz = z; iz < z + length; iz++) {
          if (this.space[ix][iy][iz]) {
            return false;
          }
        }
      }
    }
    return true;
  }

  public occupySpace(
    x: number,
    y: number,
    z: number,
    width: number,
    length: number,
    height: number
  ): void {
    // Double-check space is available
    if (!this.isSpaceAvailable(x, y, z, width, length, height)) {
      throw new Error(
        `Cannot occupy space at (${x},${y},${z}): space is already occupied`
      );
    }

    // Mark space as occupied
    for (let ix = x; ix < x + width; ix++) {
      for (let iy = y; iy < y + height; iy++) {
        for (let iz = z; iz < z + length; iz++) {
          this.space[ix][iy][iz] = true;
        }
      }
    }
  }

  public getHighestPointAt(x: number, z: number): number {
    if (x < 0 || x >= this.width || z < 0 || z >= this.length) {
      return 0;
    }

    for (let y = this.height - 1; y >= 0; y--) {
      if (this.space[x][y][z]) {
        return y + 1;
      }
    }
    return 0;
  }

  public hasAdjacentBoxesAtLevel(x: number, y: number, z: number): boolean {
    const directions = [
      [-1, 0],
      [1, 0],
      [0, -1],
      [0, 1],
    ];

    for (const [dx, dz] of directions) {
      const newX = x + dx;
      const newZ = z + dz;

      if (this.isSpaceOccupied(newX, y, newZ)) {
        return true;
      }
    }

    return false;
  }

  public getLayerUtilization(y: number): number {
    if (y < 0 || y >= this.height) return 0;

    let occupied = 0;
    const total = this.width * this.length;

    for (let x = 0; x < this.width; x++) {
      for (let z = 0; z < this.length; z++) {
        if (this.space[x][y][z]) {
          occupied++;
        }
      }
    }

    return occupied / total;
  }

  public reset(): void {
    for (let x = 0; x < this.width; x++) {
      for (let y = 0; y < this.height; y++) {
        for (let z = 0; z < this.length; z++) {
          this.space[x][y][z] = false;
        }
      }
    }
  }
  public getDimensions(): { width: number; height: number; length: number } {
    return {
      width: this.width,
      height: this.height,
      length: this.length,
    };
  }

  public validateStructure(): boolean {
    try {
      // Check first dimension
      if (!Array.isArray(this.space) || this.space.length !== this.width) {
        return false;
      }

      // Check all dimensions
      for (let x = 0; x < this.width; x++) {
        if (
          !Array.isArray(this.space[x]) ||
          this.space[x].length !== this.height
        ) {
          return false;
        }
        for (let y = 0; y < this.height; y++) {
          if (
            !Array.isArray(this.space[x][y]) ||
            this.space[x][y].length !== this.length
          ) {
            return false;
          }
          for (let z = 0; z < this.length; z++) {
            if (typeof this.space[x][y][z] !== "boolean") {
              return false;
            }
          }
        }
      }
      return true;
    } catch (error) {
      console.error("Error validating structure:", error);
      return false;
    }
  }
  public validatePlacement(
    x: number,
    y: number,
    z: number,
    width: number,
    length: number,
    height: number
  ): boolean {
    // Check boundaries
    if (
      x < 0 ||
      x + width > this.width ||
      y < 0 ||
      y + height > this.height ||
      z < 0 ||
      z + length > this.length
    ) {
      return false;
    }

    // Check for overlap with existing boxes
    if (!this.isSpaceAvailable(x, y, z, width, length, height)) {
      return false;
    }

    // If not on ground level, check support
    if (y > 0) {
      let supportedArea = 0;
      const totalArea = width * length;

      for (let ix = x; ix < x + width; ix++) {
        for (let iz = z; iz < z + length; iz++) {
          if (this.isSpaceOccupied(ix, y - 1, iz)) {
            supportedArea++;
          }
        }
      }

      if (supportedArea / totalArea < 0.6) {
        return false;
      }
    }

    return true;
  }
}

export async function loadBoxesToPlacedTable(
  quadrantWidthInches: number,
  quadrantLengthInches: number,
  maxPalletHeightInches: number,
  maxPalletWeightPounds: number,
  boxes: Distributed_Items[],
  currentPalletNo: number,
  currentUsedVolume: number,
  totalPalletVolume: number,
  selectedPallet: string,
  noOfQuadrants: number,
  threshold: number
): Promise<{ placedBoxes: PlacedBox[]; remainingBoxes: Box[] }> {
  console.log(
    `Loading boxes onto pallet ${currentPalletNo}, Pallet ${selectedPallet}`
  );
  console.log(
    `Quadrant dimensions: ${quadrantWidthInches}x${quadrantLengthInches}x${maxPalletHeightInches}, Max pallet weight: ${maxPalletWeightPounds}`
  );

  const placedBoxes: PlacedBox[] = [];
  const remainingBoxes: Box[] = [];
  let currentPalletWeight = 0;

  // Initialize or persist used volume tracking for each quadrant
  let usedVolumeByQuadrant: { [key: number]: number } = {};
  let palletSpacesByQuadrant: { [key: number]: PalletSpace } = {}; // Track PalletSpace for each quadrant
  for (let i = 1; i <= noOfQuadrants; i++) {
    usedVolumeByQuadrant[i] = 0; // Initialize used volume for each quadrant
    palletSpacesByQuadrant[i] = new PalletSpace(
      quadrantWidthInches,
      quadrantLengthInches,
      maxPalletHeightInches
    ); // Initialize PalletSpace for each quadrant
  }

  // Sort boxes by descending volume
  boxes.sort((a, b) => {
    const volumeA = (a.length || 0) * (a.width || 0) * (a.height || 0);
    const volumeB = (b.length || 0) * (b.width || 0) * (b.height || 0);
    return volumeB - volumeA;
  });

  let totalIterations = 0;
  const maxIterations = 1000;
  const maxFailedAttempts = 3;
  let failedAttempts = 0;

  // Pre-process boxes to mark oversized ones
  boxes.forEach((box) => {
    if (
      box.width > quadrantWidthInches ||
      Number(box.length) > quadrantLengthInches ||
      Number(box.height) > maxPalletHeightInches
    ) {
      box.isSkipped = true;
      console.log(
        `Box ${box.id} marked as skipped: Dimensions (${Number(
          box.width
        )}x${Number(box.length)}x${Number(
          box.height
        )}) exceed quadrant size (${quadrantWidthInches}x${quadrantLengthInches}x${maxPalletHeightInches})`
      );
    }
  });

  while (
    boxes.some((box) => !box.isPlaced && !box.isSkipped) &&
    totalIterations < maxIterations
  ) {
    let boxesPlacedThisPallet = 0;

    for (let quadrantId = 1; quadrantId <= noOfQuadrants; quadrantId++) {
      console.log(
        `Processing quadrant ${quadrantId}, Pallet ${currentPalletNo}, Iteration ${
          totalIterations + 1
        }`
      );

      // Use the persistent PalletSpace object for the current quadrant
      const palletSpace = palletSpacesByQuadrant[quadrantId];

      const quadrantBoxes = boxes.filter(
        (box) => !box.isPlaced && !box.isSkipped && box.quad_id === quadrantId
      );

      let availableVolume = totalPalletVolume; // Reset the available volume for each quadrant

      for (const box of quadrantBoxes) {
        const boxVolume =
          Number(box.length) * Number(box.width) * Number(box.height);

        if (
          currentPalletWeight + Number(box.weight) > maxPalletWeightPounds ||
          usedVolumeByQuadrant[quadrantId] + boxVolume > availableVolume
        ) {
          console.log(
            `Cannot place box ${box.id}: Would exceed weight or volume limit in quadrant ${quadrantId}`
          );
          continue;
        }

        const { placed, placedBox } = attemptToPlaceBox(
          box,
          palletSpace,
          currentPalletNo,
          quadrantId,
          box.storagetype,
          quadrantWidthInches,
          quadrantLengthInches,
          maxPalletHeightInches,
          0, // quadrantOffsetX
          0, // quadrantOffsetZ,
          threshold
        );

        if (placed && placedBox) {
          placedBoxes.push(placedBox);
          usedVolumeByQuadrant[quadrantId] += boxVolume;
          currentPalletWeight += box.weight;
          box.isPlaced = true;
          availableVolume -= boxVolume; // Deduct the volume used by this box
          boxesPlacedThisPallet++;
          console.log(
            `Placed box ${box.id} in quadrant ${quadrantId} at (${placedBox.x}, ${placedBox.y}, ${placedBox.z})`
          );
        }
      }
    }

    // Check if smaller/lighter boxes can be placed in the remaining space
    if (boxesPlacedThisPallet > 0) {
      for (let quadrantId = 1; quadrantId <= noOfQuadrants; quadrantId++) {
        const palletSpace = palletSpacesByQuadrant[quadrantId];
        const remainingBoxes = boxes.filter(
          (box) => !box.isPlaced && !box.isSkipped
        );

        for (const box of remainingBoxes) {
          const boxVolume =
            Number(box.length) * Number(box.width) * Number(box.height);

          if (
            currentPalletWeight + box.weight > maxPalletWeightPounds ||
            usedVolumeByQuadrant[quadrantId] + boxVolume > totalPalletVolume
          ) {
            continue;
          }

          const { placed, placedBox } = attemptToPlaceBox(
            box,
            palletSpace,
            currentPalletNo,
            quadrantId,
            box.storagetype,
            quadrantWidthInches,
            quadrantLengthInches,
            maxPalletHeightInches,
            0, // quadrantOffsetX
            0, // quadrantOffsetZ
            threshold
          );

          if (placed && placedBox) {
            placedBoxes.push(placedBox);
            usedVolumeByQuadrant[quadrantId] += boxVolume;
            currentPalletWeight += box.weight;
            box.isPlaced = true;
            console.log(
              `Placed remaining box ${box.id} in quadrant ${quadrantId} at (${placedBox.x}, ${placedBox.y}, ${placedBox.z})`
            );
          }
        }
      }
    }

    console.log(
      `Pallet ${currentPalletNo} complete. Placed ${boxesPlacedThisPallet} boxes.`
    );

    if (boxesPlacedThisPallet === 0) {
      failedAttempts++;
      console.log(
        `Failed attempt ${failedAttempts} to place boxes on pallet ${currentPalletNo}.`
      );

      if (failedAttempts >= maxFailedAttempts) {
        console.log("Maximum failed attempts reached. Moving to next pallet.");
        currentPalletNo++;
        currentPalletWeight = 0;
        failedAttempts = 0; // Reset failed attempts for the new pallet

        // Reset the used volume tracking for the new pallet
        usedVolumeByQuadrant = {};
        palletSpacesByQuadrant = {}; // Reset PalletSpace for the new pallet
        for (let i = 1; i <= noOfQuadrants; i++) {
          usedVolumeByQuadrant[i] = 0;
          palletSpacesByQuadrant[i] = new PalletSpace(
            quadrantWidthInches,
            quadrantLengthInches,
            maxPalletHeightInches
          ); // Initialize PalletSpace for each quadrant
        }
      }
    } else {
      failedAttempts = 0; // Reset failed attempts if we successfully placed boxes
    }

    totalIterations++;
  }

  // Add any boxes that couldn't be placed to remainingBoxes
  remainingBoxes.push(...boxes.filter((box) => !box.isPlaced || box.isSkipped));

  console.log(`Placement complete after ${totalIterations} iterations.`);
  console.log(
    `Placed ${placedBoxes.length} boxes across ${currentPalletNo} pallets`
  );
  console.log(`${remainingBoxes.length} boxes couldn't be placed`);

  return { placedBoxes, remainingBoxes };
}

function attemptToPlaceBox(
  box: Distributed_Items,
  palletSpace: PalletSpace,
  currentPalletNo: number,
  quadrantId: number,
  storagetype: string,
  quadrantWidth: number,
  quadrantLength: number,
  maxPalletHeightInches: number,
  quadrantOffsetX: number,
  quadrantOffsetZ: number,
  supportThreshold: number
): { placed: boolean; placedBox?: PlacedBox } {
  const orientations = [
    { width: box.width, length: box.length, rotation: 0 },
    { width: box.length, length: box.width, rotation: 90 },
  ];

  //const supportThreshold = 0.85; // 60% support required

  for (let y = 0; y < maxPalletHeightInches - Number(box.height) + 1; y++) {
    for (let x = 0; x < quadrantWidth - Number(box.width) + 1; x++) {
      for (let z = 0; z < quadrantLength - Number(box.length) + 1; z++) {
        for (const orientation of orientations) {
          if (
            y === 0 ||
            palletSpace.isSpaceAvailable(
              x,
              y,
              z,
              orientation.width,
              orientation.length,
              box.height
            )
          ) {
            // Ensure at least 60% of the base is supported if placing above the ground layer
            if (y > 0) {
              const baseSupport = calculateBaseSupport(
                x,
                y - 1,
                z,
                orientation.width,
                orientation.length,
                palletSpace
              );
              if (baseSupport < supportThreshold) {
                continue; // Skip if less than 60% of the base is supported
              }
            }

            // Check that the entire space is free
            if (
              !palletSpace.isSpaceAvailable(
                x,
                y,
                z,
                orientation.width,
                orientation.length,
                box.height
              )
            ) {
              continue; // Skip if space is not available
            }

            // Ensure tight stacking by rounding or flooring positions
            const adjustedY = Math.floor(y); // Adjust to eliminate any floating-point error

            const placedBox: PlacedBox = {
              id: box.id ?? generateUniqueId(),
              name: box.name,
              nsn: box.nsn,
              niin: box.niin,
              nomenclature: box.nomenclature,
              qty: box.qty,
              authorizedqty: box.authorizedqty,
              weight: Number(box.weight),
              maxsupportedweight: Number(box.maxsupportedweight),
              length: Number(box.length),
              width: Number(box.width),
              height: Number(box.height),
              level: Math.floor(adjustedY / 12) + 1,
              levelMaxHeight: adjustedY + Number(box.height),
              quadrantId: quadrantId,
              palletid: currentPalletNo,
              storagetype: storagetype,
              position_no: 1,
              x: x + quadrantOffsetX,
              y: adjustedY,
              z: z + quadrantOffsetZ,
              spillNo: 1,
              rotation: orientation.rotation,
              palletno: currentPalletNo,
            };

            // Mark the space as occupied
            palletSpace.occupySpace(
              x,
              adjustedY,
              z,
              orientation.width,
              orientation.length,
              Number(box.height)
            );

            return { placed: true, placedBox };
          }
        }
      }
    }
  }

  return { placed: false };
}

// loadBoxesToPlacedTable for 456LI

export async function loadBoxesToPlacedTable_CenterAlign(
  quadrantWidthInches: number,
  quadrantLengthInches: number,
  maxPalletHeightInches: number,
  maxPalletWeightPounds: number,
  boxes: Distributed_Items[],
  currentPalletNo: number,
  currentUsedVolume: number,
  totalPalletVolume: number,
  selectedPallet: string,
  noOfQuadrants: number,
  threshold: number
): Promise<{ placedBoxes: PlacedBox[]; remainingBoxes: Box[] }> {
  console.log(`Starting box placement with ${boxes.length} boxes`);

  const placedBoxes: PlacedBox[] = [];
  const remainingBoxes: Box[] = [];
  let currentPalletWeight = 0;
  let palletSpacesByQuadrant: { [key: number]: PalletSpace } = {};

  // Initialize first pallet
  function initializePallet() {
    palletSpacesByQuadrant = {};
    for (let i = 1; i <= noOfQuadrants; i++) {
      palletSpacesByQuadrant[i] = new PalletSpace(
        quadrantWidthInches,
        quadrantLengthInches,
        maxPalletHeightInches
      );
    }
    currentPalletWeight = 0;
    return { x: quadrantWidthInches / 2, z: quadrantLengthInches / 2 };
  }

  // Sort boxes by weight and volume
  boxes.sort((a, b) => {
    if (b.weight !== a.weight) {
      return b.weight - a.weight;
    }
    const volumeA = a.length * a.width * a.height;
    const volumeB = b.length * b.width * b.height;
    return volumeB - volumeA;
  });

  // Skip oversized boxes
  boxes.forEach((box) => {
    if (
      Number(box.width) > quadrantWidthInches ||
      Number(box.length) > quadrantLengthInches ||
      Number(box.height) > maxPalletHeightInches
    ) {
      box.isSkipped = true;
      console.log(`Box ${box.id} skipped: too large for pallet`);
    }
  });

  let currentCenterOfMass = initializePallet();
  let failedAttempts = 0;
  const maxFailedAttempts = 3;

  // Process each box
  for (let i = 0; i < boxes.length; i++) {
    const box = boxes[i];
    if (box.isSkipped || box.isPlaced) continue;

    // Check if box would exceed weight limit
    if (currentPalletWeight + Number(box.weight) > maxPalletWeightPounds) {
      console.log(`Starting new pallet due to weight limit`);
      currentPalletNo++;
      currentCenterOfMass = initializePallet();
      failedAttempts = 0;
      i--; // Retry the same box on new pallet
      continue;
    }

    let placed = false;
    // Try each quadrant
    for (
      let quadrantId = 1;
      quadrantId <= noOfQuadrants && !placed;
      quadrantId++
    ) {
      const result = attemptToPlaceBox_CenterAlign(
        box,
        palletSpacesByQuadrant[quadrantId],
        currentPalletNo,
        quadrantId,
        box.storagetype,
        quadrantWidthInches,
        quadrantLengthInches,
        maxPalletHeightInches,
        ((quadrantId - 1) % 2) * quadrantWidthInches, // X offset for quadrant
        Math.floor((quadrantId - 1) / 2) * quadrantLengthInches, // Z offset for quadrant
        threshold,
        currentCenterOfMass,
        currentPalletWeight,
        currentPalletWeight === 0 // Force center placement only for first box on pallet
      );

      if (result.placed && result.placedBox) {
        placedBoxes.push(result.placedBox);
        currentPalletWeight += Number(box.weight);
        box.isPlaced = true;
        placed = true;
        currentCenterOfMass = result.newCenterOfMass;
        failedAttempts = 0;
        console.log(
          `Placed box ${box.id} on pallet ${currentPalletNo}, quadrant ${quadrantId}`
        );
      }
    }

    // If box couldn't be placed, try starting a new pallet
    if (!placed) {
      failedAttempts++;
      console.log(`Failed to place box ${box.id}, attempt ${failedAttempts}`);

      if (failedAttempts >= maxFailedAttempts) {
        console.log(
          `Starting new pallet after ${maxFailedAttempts} failed attempts`
        );
        currentPalletNo++;
        currentCenterOfMass = initializePallet();
        failedAttempts = 0;
        i--; // Retry the same box on new pallet
      }
    }
  }

  // Add any unplaced boxes to remainingBoxes
  remainingBoxes.push(
    ...boxes.filter((box) => !box.isPlaced && !box.isSkipped)
  );

  console.log(
    `Placement complete: ${placedBoxes.length} boxes placed across ${currentPalletNo} pallets`
  );
  console.log(`${remainingBoxes.length} boxes couldn't be placed`);

  return { placedBoxes, remainingBoxes };
}

interface PlacementResult {
  placed: boolean;
  placedBox?: PlacedBox;
  newCenterOfMass: { x: number; z: number };
  newTotalWeight: number;
}

interface BoxOrientation {
  width: number;
  length: number;
  rotation: number;
}

interface BoxPosition {
  x: number;
  y: number;
  z: number;
  orientation: BoxOrientation;
}

interface PlacementResult {
  placed: boolean;
  placedBox?: PlacedBox;
  newCenterOfMass: { x: number; z: number };
  newTotalWeight: number;
}

// Modified attemptToPlaceBox_CenterAlign function to handle vertical stacking
function attemptToPlaceBox_CenterAlign(
  box: Distributed_Items,
  palletSpace: PalletSpace,
  currentPalletNo: number,
  quadrantId: number,
  storagetype: string,
  palletWidth: number,
  palletLength: number,
  maxPalletHeightInches: number,
  quadrantOffsetX: number,
  quadrantOffsetZ: number,
  supportThreshold: number,
  currentCenterOfMass: { x: number; z: number },
  currentTotalWeight: number,
  forceCenterPlacement: boolean = false
): PlacementResult {
  const orientations: BoxOrientation[] = [
    { width: Number(box.width), length: box.length, rotation: 0 },
    { width: Number(box.length), length: box.width, rotation: 90 },
  ];

  const palletCenterX = palletWidth / 2;
  const palletCenterZ = palletLength / 2;

  let bestPosition: BoxPosition | null = null;
  let bestScore = Number.POSITIVE_INFINITY;

  // Function to verify space is completely free
  function isSpaceFree(
    x: number,
    y: number,
    z: number,
    width: number,
    length: number,
    height: number
  ): boolean {
    // Check boundaries
    if (
      x < 0 ||
      x + width > palletWidth ||
      y < 0 ||
      y + height > maxPalletHeightInches ||
      z < 0 ||
      z + length > palletLength
    ) {
      return false;
    }

    // Check every point in the space
    for (let ix = x; ix < x + width; ix++) {
      for (let iy = y; iy < y + height; iy++) {
        for (let iz = z; iz < z + length; iz++) {
          if (palletSpace.isSpaceOccupied(ix, iy, iz)) {
            return false;
          }
        }
      }
    }
    return true;
  }

  // Try center placement first if required
  if (forceCenterPlacement || currentTotalWeight === 0) {
    for (const orientation of orientations) {
      const centerX = Math.floor(palletCenterX - orientation.width / 2);
      const centerZ = Math.floor(palletCenterZ - orientation.length / 2);

      if (
        palletSpace.validatePlacement(
          centerX,
          0,
          centerZ,
          orientation.width,
          orientation.length,
          box.height
        )
      ) {
        bestPosition = {
          x: centerX,
          y: 0,
          z: centerZ,
          orientation,
        };
        break;
      }
    }
  }

  // If no center placement, try other positions
  if (!bestPosition) {
    // Generate positions in a grid pattern
    const stepX = Math.max(1, Math.floor(Number(box.width) / 2));
    const stepZ = Math.max(1, Math.floor(Number(box.length) / 2));

    for (let x = 0; x <= palletWidth - Number(box.width); x += stepX) {
      for (let z = 0; z <= palletLength - Number(box.length); z += stepZ) {
        for (const orientation of orientations) {
          // Skip if position would exceed boundaries
          if (
            x + orientation.width > palletWidth ||
            z + orientation.length > palletLength
          ) {
            continue;
          }

          // Try stacking on top of existing boxes
          let y = 0;
          const maxY = maxPalletHeightInches - Number(box.height);

          while (y <= maxY) {
            if (
              isSpaceFree(
                x,
                y,
                z,
                orientation.width,
                orientation.length,
                Number(box.height)
              )
            ) {
              // Check support if not on ground
              if (
                y === 0 ||
                hasAdequateSupport(
                  x,
                  y,
                  z,
                  orientation.width,
                  orientation.length,
                  palletSpace
                )
              ) {
                const score = calculatePositionScore(
                  x,
                  y,
                  z,
                  orientation,
                  currentCenterOfMass,
                  currentTotalWeight,
                  box.weight,
                  palletCenterX,
                  palletCenterZ
                );

                if (score < bestScore) {
                  bestScore = score;
                  bestPosition = {
                    x,
                    y,
                    z,
                    orientation,
                  };
                }
              }
            }

            // Find next potential height
            y = findNextPotentialHeight(
              x,
              y + 1,
              z,
              orientation.width,
              orientation.length,
              palletSpace
            );
          }
        }
      }
    }
  }

  if (bestPosition) {
    // Create placed box and occupy space
    const newTotalWeight = currentTotalWeight + Number(box.weight);
    const boxCenterX = bestPosition.x + bestPosition.orientation.width / 2;
    const boxCenterZ = bestPosition.z + bestPosition.orientation.length / 2;

    const newCenterOfMassX =
      (currentCenterOfMass.x * currentTotalWeight +
        boxCenterX * Number(box.weight)) /
      newTotalWeight;
    const newCenterOfMassZ =
      (currentCenterOfMass.z * currentTotalWeight +
        boxCenterZ * Number(box.weight)) /
      newTotalWeight;

    const placedBox: PlacedBox = {
      id: box.id ?? generateUniqueId(),
      name: box.name,
      nsn: box.nsn,
      niin: box.niin,
      nomenclature: box.nomenclature,
      qty: box.qty,
      authorizedqty: box.authorizedqty,
      weight: Number(box.weight),
      length: bestPosition.orientation.length,
      width: bestPosition.orientation.width,
      height: Number(box.height),
      level: Math.floor(bestPosition.y / 12) + 1,
      levelMaxHeight: bestPosition.y + Number(box.height),
      quadrantId: quadrantId,
      palletid: currentPalletNo,
      storagetype: storagetype,
      position_no: 1,
      x: bestPosition.x + quadrantOffsetX,
      y: bestPosition.y,
      z: bestPosition.z + quadrantOffsetZ,
      spillNo: 1,
      rotation: bestPosition.orientation.rotation,
      palletno: currentPalletNo,
    };

    // Double-check space is still free before occupying
    if (
      isSpaceFree(
        bestPosition.x,
        bestPosition.y,
        bestPosition.z,
        bestPosition.orientation.width,
        bestPosition.orientation.length,
        Number(box.height)
      )
    ) {
      palletSpace.occupySpace(
        bestPosition.x,
        bestPosition.y,
        bestPosition.z,
        bestPosition.orientation.width,
        bestPosition.orientation.length,
        Number(box.height)
      );

      console.log(
        `Successfully placed box at (${bestPosition.x}, ${bestPosition.y}, ${bestPosition.z})`
      );
      return {
        placed: true,
        placedBox,
        newCenterOfMass: { x: newCenterOfMassX, z: newCenterOfMassZ },
        newTotalWeight,
      };
    } else {
      console.log("Space was occupied when attempting to place box");
      return {
        placed: false,
        newCenterOfMass: currentCenterOfMass,
        newTotalWeight: currentTotalWeight,
      };
    }
  }

  return {
    placed: false,
    newCenterOfMass: currentCenterOfMass,
    newTotalWeight: currentTotalWeight,
  };
}

function hasAdequateSupport(
  x: number,
  y: number,
  z: number,
  width: number,
  length: number,
  palletSpace: PalletSpace
): boolean {
  const totalArea = width * length;
  let supportedArea = 0;

  for (let ix = x; ix < x + width; ix++) {
    for (let iz = z; iz < z + length; iz++) {
      if (palletSpace.isSpaceOccupied(ix, y - 1, iz)) {
        supportedArea++;
      }
    }
  }

  return supportedArea / totalArea >= 0.6;
}

function findNextPotentialHeight(
  x: number,
  startY: number,
  z: number,
  width: number,
  length: number,
  palletSpace: PalletSpace
): number {
  let maxHeight = startY;

  for (let ix = x; ix < x + width; ix++) {
    for (let iz = z; iz < z + length; iz++) {
      const height = palletSpace.getHighestPointAt(ix, iz);
      maxHeight = Math.max(maxHeight, height);
    }
  }

  return maxHeight;
}

function calculatePositionScore(
  x: number,
  y: number,
  z: number,
  orientation: BoxOrientation,
  currentCenterOfMass: { x: number; z: number },
  currentTotalWeight: number,
  boxWeight: number,
  palletCenterX: number,
  palletCenterZ: number
): number {
  const boxCenterX = x + orientation.width / 2;
  const boxCenterZ = z + orientation.length / 2;

  // Calculate weight distribution impact
  const newTotalWeight = currentTotalWeight + Number(boxWeight);
  const newCenterX =
    (currentCenterOfMass.x * currentTotalWeight + boxCenterX * boxWeight) /
    newTotalWeight;
  const newCenterZ =
    (currentCenterOfMass.z * currentTotalWeight + boxCenterZ * boxWeight) /
    newTotalWeight;

  // Distance from pallet center
  const centerDeviation = Math.sqrt(
    Math.pow(newCenterX - palletCenterX, 2) +
      Math.pow(newCenterZ - palletCenterZ, 2)
  );

  // Prefer lower positions but don't completely discourage stacking
  const heightPenalty = y * 0.1;

  return centerDeviation + heightPenalty;
}

// Function to calculate the support for the box's base
function calculateBaseSupport(
  x: number,
  y: number,
  z: number,
  width: number,
  length: number,
  palletSpace: PalletSpace
): number {
  let supportedArea = 0;
  const totalArea = width * length;

  for (let ix = x; ix < x + width; ix++) {
    for (let iz = z; iz < z + length; iz++) {
      if (palletSpace.isSpaceOccupied(ix, y, iz)) {
        supportedArea++;
      }
    }
  }

  return supportedArea / totalArea;
}

// Helper function to generate a unique ID
function generateUniqueId(): number {
  return Date.now() + Math.floor(Math.random() * 1000);
}

export async function updatePlacedBoxes(
  placedBoxes: PlacedBox[],
  currentPalletNo: number
): Promise<void> {
  console.log("BEGIN updatePlacedBoxes", { placedBoxes, currentPalletNo });

  try {
    // Update placed_boxes table via API
    const placedBoxesPayload = placedBoxes.map((box) => ({
      name: box.name,
      nsn: box.nsn,
      niin: box.niin,
      nomenclature: box.nomenclature,
      qty: box.qty,
      authorizedqty: box.authorizedqty,
      weight: Number(box.weight),
      length: Number(box.length),
      width: Number(box.width),
      height: Number(box.height),
      level: box.level,
      levelMaxHeight: box.levelMaxHeight,
      quadrantId: box.quadrantId,
      palletid: box.palletid,
      storagetype: box.storagetype,
      position_no: box.position_no,
      x: box.x,
      y: box.y,
      z: box.z,
      spillNo: 1,
      rotation: box.rotation,
      palletno: box.palletno,
    }));

    if (placedBoxesPayload && placedBoxesPayload.length > 0) {
      console.log("Updating placed_boxes via API:", placedBoxesPayload);

      try {
        const placedBoxesResponse = await api.post(
          `/api/placed_boxes/bulkInsert`,
          runBody({ placedBoxes: placedBoxesPayload })
        );
        console.log(
          "Placed boxes updated successfully:",
          placedBoxesResponse.data
        );
      } catch (error) {
        console.error("Error updating placed_boxes via API:", error);
      }
    } else {
      console.log("No placed boxes to update.");
    }

    // Update distributed_items table via API only if there is data
    const distributedItemsPayload = placedBoxes?.map((box) => box.id) || [];
    if (distributedItemsPayload.length > 0) {
      console.log(
        "Updating distributed_items via API:",
        distributedItemsPayload
      );

      try {
        const distributedItemsResponse = await api.put(
          `/api/distributed_items/markPlaced`,
          runBody({ boxIds: distributedItemsPayload })
        );
        console.log(
          "Distributed items updated successfully:",
          distributedItemsResponse.data
        );
      } catch (error) {
        console.error("Error updating distributed_items via API:", error);
      }
    } else {
      console.log("No distributed items to update.");
    }
  } catch (error) {
    if (error instanceof Error) {
      console.error("Error updating placed boxes:", error.message);
    } else {
      console.error("Unexpected error:", error);
    }
    throw error;
  }
}

// The getTotalWeight function remains unchanged as it already considers the pallet number and storage type

export async function getTotalWeightArray(
  isu90tabCount: number,
  flat463LtabCount: number,
  c463LtabCount: number,
  nfevtabCount: number,
  pallet80x120tabCount: number,
  pallet100x120tabCount: number,
): Promise<number[]> {

  try {
    const totalWeights: number[] = [];
    const fetchTotalWeight = async (
      palletid: number,
      storagetype: string
    ): Promise<number> => {



      // 1. Get the current authentication login sessionId from storage or state
      const currentRunId = getCurrentRunId();


      const response = await api.get(`/api/placed_boxes_view/storagetype`, {
        params: runParams({ palletid, storagetype }, currentRunId),
      });
      console.log("response.data ", response.data);
      return response.data[0].sum || 0;
    };

    // Process QuadPallet pallets
    for (let i = 1; i <= isu90tabCount; i++) {
      const totalWeight = await fetchTotalWeight(i, "QuadPallet");
      totalWeights.push(Math.round(totalWeight * 100) / 100);
    }

    console.log("fetchTotalWeight ", totalWeights);

    // Process 463L pallets
    for (let i = 1; i <= flat463LtabCount; i++) {
      const totalWeight = await fetchTotalWeight(i, "463L");
      totalWeights.push(Math.round(totalWeight * 100) / 100);
    }

    console.log("fetchTotalWeight ", totalWeights);

    // Process C463L pallets
    for (let i = 1; i <= c463LtabCount; i++) {
      const totalWeight = await fetchTotalWeight(i, "C463L");
      totalWeights.push(Math.round(totalWeight * 100) / 100);
    }

    // Process NFEV pallets
    for (let i = 1; i <= nfevtabCount; i++) {
      const totalWeight = await fetchTotalWeight(i, "NFEV");
      totalWeights.push(Math.round(totalWeight * 100) / 100);
    }

    console.log("fetchTotalWeight ", totalWeights);

    // Process 80x120 pallets
    for (let i = 1; i <= pallet80x120tabCount; i++) {
      const totalWeight = await fetchTotalWeight(i, "80x120");
      totalWeights.push(Math.round(totalWeight * 100) / 100);
    }

    console.log("fetchTotalWeight ", totalWeights);

    // Process 100x120 pallets
    for (let i = 1; i <= pallet100x120tabCount; i++) {
      const totalWeight = await fetchTotalWeight(i, "100x120");
      totalWeights.push(Math.round(totalWeight * 100) / 100);
    }

    console.log("fetchTotalWeight ", totalWeights);

    console.log(`Total weights calculated: ${totalWeights}`);
    return totalWeights;
  } catch (error) {
    console.error("Failed to calculate total weights:", error);
    throw error; // Propagate error to the caller
  }
}

export async function getTotalWeight(storagetype: string, palletid: number, currentRunId: string) {
  try {

    const response = await api.get(`/api/pallet_usage`, {
      params: runParams({ storagetype, palletid }, currentRunId)
    });

    let weightPlaced: number = 0;

    // 1. Check if response.data is an array and has at least one item
    // 2. Use optional chaining (?.) to safely access the property
    if (Array.isArray(response.data) && response.data.length > 0) {
      weightPlaced = response.data[0]?.placedweight ?? 0;
    }
    console.log("hit get Total weight");
    console.log("weightPlaced"+weightPlaced);


    return weightPlaced;
  } catch (error) {
    console.error("Failed to calculate total weights:", error);
    return 0;
  }
}

export interface PalletUsageData {
  percent: number;
  weightPlaced: number;
}

export async function getPercentUsage(
  storagetype: string,
  palletid: number,
  currentRunId?: string
): Promise<PalletUsageData> {
  try {
    const response = await api.get(`/api/pallet_usage`, {
      params: runParams({ storagetype, palletid }, currentRunId)
    });

    let percent: number = 0;
    let weightPlaced: number = 0;

    if (Array.isArray(response.data) && response.data.length > 0) {
      percent = response.data[0]?.utilization_percentage_volume ?? 0;
      weightPlaced = response.data[0]?.placedweight ?? 0;
    }

    return { percent, weightPlaced };
  } catch (error) {
    console.error("Error getting percent from placed_boxes table:", error);
    // Crucial: Fallback must be an object matching PalletUsageData, NOT a primitive number
    return { percent: 0, weightPlaced: 0 };
  }
}


// ===========================================================================
// QuadPallet quadrant refresh
// algorithm against the boxes currently in (palletNoSeq, quadrant) using
// their latest dimensions from placed_boxes, then writes the result back
// to placed_boxes + placed_boxes_view.
// ===========================================================================
export async function refreshISU90Quadrant(
  storageType: string,
  palletNoSeq: number,
  quadrant: string | number,
  runId: string,
  options: { fullPallet?: boolean } = {}
): Promise<any> {
  const activeRunId = String(runId || getCurrentRunId()).trim();
  const body: any = runBody(
    {
      selectedStorageType: storageType,
      storageType,
      palletNo: palletNoSeq,
      palletNoSeq,
    },
    activeRunId
  );
  if (typeof quadrant === "number") body.quadId = quadrant;
  else body.quadrant = quadrant;
  // Opt-in: repack the WHOLE pallet (all quadrants, shelving included).
  // Default stays quadrant-scoped; boxes in other quadrants never move.
  if (options.fullPallet === true) body.fullPallet = true;

  const response = await api.post(`/api/quadPallet/refresh-quadrant`, body);
  return response.data;
}

// ===========================================================================
// Update a placed box's dimensions (width / length / height) in placed_boxes.
// Called from InfoBox when the user clicks Save on the edit panel.
//
// placedBoxId = placed_boxes.id (the row PK). All dimensions are in the same
// units the InfoBox displays (inches for QuadPallet).
// ===========================================================================
export async function updatePlacedBoxDimensions(
  placedBoxId: number,
  dims: { width: number; length: number; height: number }
): Promise<any> {
  const response = await api.patch(
    `/api/placed_boxes/${placedBoxId}/dimensions`,
    runBody({
      width: Number(dims.width),
      length: Number(dims.length),
      height: Number(dims.height),
    })
  );
  return response.data;
}


export async function getShelves() {

  const runId =localStorage.getItem('runId')

  const response = await api.get(`/api/placed_shelves`, {
      params: runId ? { runId } : undefined
    });
  const shelvesdata = response.data;
  


  return shelvesdata;
}